import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import GetAmbulanceCard from "../components/GetAmbulancecard";
import ChooseAmbulanceDuplicate from "../components/ChooseAmbulancecardDuplicate";
import MapBackground from "../components/mapCompoent";
import SuccessModal from "../components/ui/SuccessModal";
import ErrorModal from "../components/ui/ErrorModal";
import OpenToOtherFleetsModal from "../components/ambulance/OpenToOtherFleetsModal";
import AmbulanceBookingConfirmationModal from "../components/ambulance/AmbulanceBookingConfirmationModal";
import PaymentInitiationModal from "../components/payment/modals/PaymentInitiationModal";
import PaymentVerificationModal from "../components/payment/modals/PaymentVerificationModal";
import PaymentOrderCreationModal from "../components/payment/modals/PaymentOrderCreationModal";
import AutomaticAssignmentPopup from "../components/ambulance/AutomaticAssignmentPopup";
import NotificationContainer from "../components/ui/NotificationContainer";
import apiService from "../services/apiService";
import hospitalService from "../services/hospitalService";
import { bookingService, authService } from "../services";
import { useAuth } from "../contexts/AuthContext";
import useNotifications from "../hooks/useNotifications";
import { API_CONFIG } from "../config/api";

// Import ambulance images
import ambulanceImage1 from "../svg/Rectangle.png";
import ambulanceImage2 from "../svg/Rectangle-2.png";
import ambulanceImage3 from "../svg/Rectangle-3.png";
import ambulanceImage4 from "../svg/Rectangle-4.png";
import ambulanceImage5 from "../svg/Rectangle-5.png";
import ambulanceImage6 from "../svg/Rectangle-6.png";

// Array of ambulance images
const ambulanceImages = [ambulanceImage1, ambulanceImage2, ambulanceImage3, ambulanceImage4, ambulanceImage5, ambulanceImage6];

// Helper functions for ambulance card styling (matching ChooseAmbulanceDuplicate)
const getAmbulanceCardClassName = (isSelected) => {
  if (isSelected) {
    return "relative flex items-start gap-3 border rounded-lg p-2.5 cursor-pointer transition-all overflow-hidden border-blue-400 bg-blue-500/20 backdrop-blur-sm";
  }
  return "relative flex items-start gap-3 border rounded-lg p-2.5 cursor-pointer transition-all overflow-hidden border-white/30 bg-black/30 backdrop-blur-sm hover:border-white/50 hover:bg-black/40";
};

const getAmbulanceNameClassName = (isSelected) => {
  return isSelected 
    ? "text-sm font-semibold text-blue-100 leading-tight break-words"
    : "text-sm font-semibold text-white leading-tight break-words";
};

const getFareDisplayClassName = (isSelected) => {
  return isSelected
    ? "text-sm font-bold text-blue-100"
    : "text-sm font-bold text-white";
};

const renderFareDisplay = (isLoadingFare, estimatedFare) => {
  if (isLoadingFare) {
    return (
      <div className="flex items-center gap-1">
        <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
        <span className="text-xs text-white">Loading...</span>
      </div>
    );
  }
  
  if (estimatedFare && estimatedFare !== "Calculating...") {
    return `₹${estimatedFare}`;
  }
  
  return "₹--";
};

/**
 * Duplicate Reusable AmbulanceInstantBooking Component
 *
 * @param {Object} props - Component props
 * @param {boolean} props.showMapBackground - Show/hide map background (default: true)
 * @param {boolean} props.showGetAmbulanceCard - Show/hide GetAmbulanceCard (default: true)
 * @param {boolean} props.showChooseAmbulance - Show/hide ChooseAmbulance (default: true)
 * @param {Function} props.onBookingChange - Callback for booking changes (default: console.log)
 * @param {string} props.className - Additional CSS classes for container
 * @param {string} props.contentClassName - Additional CSS classes for content area
 * @param {Object} props.mapCenter - Map center coordinates (default: Ahmedabad)
 * @param {number} props.mapZoom - Map zoom level (default: 12)
 * @param {string} props.layout - Layout type: "default", "compact", "full-width" (default: "default")
 */
const AmbulanceInstantBookingDuplicate = ({
  showMapBackground = true,
  showGetAmbulanceCard = true,
  showChooseAmbulance = true,
  onBookingChange = (data) => console.log(data),
  onTrackBookingClick, // Callback for track booking button
  className = "",
  contentClassName = "",
  mapCenter = { lat: 23.0225, lng: 72.5714 },
  mapZoom = 12,
  layout = "default", // "default", "compact", "full-width"
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Notification system
  const { notification, showError, showSuccess } = useNotifications();

  // State for selected ambulance
  const [selectedAmbulance, setSelectedAmbulance] = useState(null);
  
  // State for allocation type to show ambulance type above map (default: Automatic)
  const [allocationType, setAllocationType] = useState("Automatic");
  // Reset counter to force child component reset when cancel is clicked
  const [allocationResetCounter, setAllocationResetCounter] = useState(0);
  
  // State for ambulance types from choose ambulance section
  const [availableAmbulanceTypes, setAvailableAmbulanceTypes] = useState([]);
  
  // Store pending ambulance types (received before confirmation)
  const pendingAmbulanceTypesRef = useRef([]);
  
  // State for candidates from manual search (actual vehicles with drivers)
  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [candidatesError, setCandidatesError] = useState(null);
  const [hasSearchedCandidates, setHasSearchedCandidates] = useState(false); // Track if manual search was performed
  const [candidateSearchQuery, setCandidateSearchQuery] = useState(''); // UI-based search filter for candidates
  
  // State for allocation status polling
  const [allocationStatus, setAllocationStatus] = useState(null);
  const [isPollingAllocation, setIsPollingAllocation] = useState(false);
  const [currentBookingId, setCurrentBookingId] = useState(null);
  const [assigningCandidateId, setAssigningCandidateId] = useState(null); // Track which specific candidate is being assigned
  const [pricingInfoOpenKey, setPricingInfoOpenKey] = useState(null); // 'manual_vehicleId' | 'auto_typeId' for pricing breakdown popover
  const [pricingInfoData, setPricingInfoData] = useState(null); // { pricing_source, pricing_model, pricing_breakdown } for fixed overlay
  const allocationPollingIntervalRef = useRef(null);
  const lastTriggerNextDriverCallRef = useRef(null); // Track last triggerNextDriver call time to prevent rapid calls
  
  // State for fare estimates and vendor names (to match ChooseAmbulanceDuplicate design)
  const [fareEstimates, setFareEstimates] = useState({});
  const [fareLoading, setFareLoading] = useState({});
  const [vendorNamesMap, setVendorNamesMap] = useState({});

  // Filter states for manual allocation
  const [selectedPartner, setSelectedPartner] = useState("all");
  const [kmRange, setKmRange] = useState(50);
  const [selectedAmbulanceTypes, setSelectedAmbulanceTypes] = useState([]);
  
  // Store all ambulance types before filtering
  const [allAmbulanceTypes, setAllAmbulanceTypes] = useState([]);

  // State for automatic allocation countdown timers
  const [automaticAllocationActive, setAutomaticAllocationActive] = useState(false);
  const [currentAutomaticIndex, setCurrentAutomaticIndex] = useState(0);
  const [automaticCountdown, setAutomaticCountdown] = useState(30);
  const [timerRestartTrigger, setTimerRestartTrigger] = useState(0); // State to trigger timer restart
  const [automaticSelectedDriver, setAutomaticSelectedDriver] = useState(null);
  const [automaticSelectionConfirmed, setAutomaticSelectionConfirmed] = useState(false);
  const [currentPageIndex, setCurrentPageIndex] = useState(0); // Track which page of 3 cards we're showing
  const automaticTimerRef = useRef(null);
  const automaticTimerStartTimeRef = useRef(null); // Store when timer started (timestamp-based)
  const lastDisplayedAutomaticCountdownRef = useRef(null); // Track last displayed countdown value to prevent unnecessary updates
  const automaticScrollContainerRef = useRef(null);
  const handleAutomaticTimeoutRef = useRef(null);
  
  // State to track if auto-allocation has been confirmed (after clicking Yes in modal)
  const [autoAllocationConfirmed, setAutoAllocationConfirmed] = useState(false);
  
  // State for automatic assignment popup
  const [automaticAssignmentPopup, setAutomaticAssignmentPopup] = useState({
    isOpen: false,
    ambulance: null,
    driver: null,
    timer: 30,
    bookingId: null
  });
  
  // Ref to store the manual allocation modal opening function
  const openManualAllocationModalRef = useRef(null);
  
  // Ref to store current paymentType from ChooseAmbulance card component
  const currentPaymentTypeRef = useRef(null);

  // Stable callback so child effect doesn't re-run every parent re-render (stops log/state loop)
  const handlePaymentTypeChange = useCallback((paymentType) => {
    currentPaymentTypeRef.current = paymentType;
    setBookingFormData((prev) =>
      prev?.paymentType === paymentType ? prev : { ...prev, paymentType }
    );
  }, []);

  // Effect to initialize allocation type callback
  useEffect(() => {
    // No default allocation type - user must choose
  }, []);

  // Effect to populate sample vendor names and fare data when ambulance types change
  useEffect(() => {
    if (availableAmbulanceTypes.length > 0) {
      const sampleVendorNames = {};
      const sampleFareLoading = {};
      
      availableAmbulanceTypes.forEach((ambulance, index) => {
        const vendorNames = [
          "MedCare Ambulance Services",
          "LifeLine Emergency Care", 
          "RapidResponse Medical",
          "CityWide Ambulance Network",
          "Emergency Medical Solutions",
          "HealthFirst Ambulance Co."
        ];
        
        sampleVendorNames[ambulance.type_id] = vendorNames[index % vendorNames.length];
        sampleFareLoading[ambulance.type_id] = false; // Not loading by default
      });
      
      setVendorNamesMap(sampleVendorNames);
      setFareLoading(sampleFareLoading);
    }
  }, [availableAmbulanceTypes]);

  // Function to trigger booking when button is clicked
  const handleBookingButtonClick = () => {
    console.log("📍 Book Ambulance button clicked!");
    if (!selectedAmbulance) {
      setError("Please select an ambulance first");
      return;
    }
    if (!bookingFormData) {
      setError("Booking form data is missing");
      return;
    }
    // Trigger the confirmation
    handleConfirmSelection(selectedAmbulance, bookingFormData);
  };

  // State for location coordinates
  const [pickupCoordinates, setPickupCoordinates] = useState(null);
  const [dropCoordinates, setDropCoordinates] = useState(null);

  // Function to filter ambulance types based on selected filters
  const filterAmbulanceTypes = useCallback((types) => {
    if (!types || types.length === 0) {
      setAvailableAmbulanceTypes([]);
      return;
    }

    let filtered = [...types];

    // Filter by partner if selected (not "all" or "all_partners")
    if (selectedPartner !== "all" && selectedPartner !== "all_partners") {
      filtered = filtered.filter(ambulance => {
        const operatorId = ambulance.operator_id || ambulance.fleetId || ambulance.fleetOperatorId;
        return String(operatorId) === String(selectedPartner);
      });
    }

    // Filter by distance if kmRange is set and pickup coordinates are available
    if (pickupCoordinates && kmRange && kmRange < 50) {
      filtered = filtered.filter(ambulance => {
        if (ambulance.distanceFromPickup !== undefined) {
          return ambulance.distanceFromPickup <= kmRange;
        }
        return true; // Keep if distance not available
      });
    }

    // Filter by ambulance type if types are selected
    if (selectedAmbulanceTypes.length > 0) {
      filtered = filtered.filter(ambulance => {
        const typeId = ambulance.type_id || ambulance.typeId || ambulance.id;
        return selectedAmbulanceTypes.includes(typeId);
      });
    }

    // If partner is "all" and no ambulance types selected, show all
    // Otherwise, show filtered results
    setAvailableAmbulanceTypes(filtered);
  }, [selectedPartner, kmRange, selectedAmbulanceTypes, pickupCoordinates]);

  // Note: Filtering is now handled in the child component (ChooseAmbulancecardDuplicate)
  // The child sends already-filtered types via onAmbulanceTypesChange
  // So we don't need to filter again here - the types are already filtered

  // Debug: Log when conditions for showing available ambulance UI change
  useEffect(() => {
    console.log("🔍 [Map UI Debug] Conditions check:", {
      hasPickup: !!pickupCoordinates,
      hasDrop: !!dropCoordinates,
      availableCount: availableAmbulanceTypes.length,
      allocationType,
      autoAllocationConfirmed,
      shouldShow: pickupCoordinates && dropCoordinates && autoAllocationConfirmed && allocationType === "Automatic" && availableAmbulanceTypes.length > 0
    });
  }, [pickupCoordinates, dropCoordinates, availableAmbulanceTypes.length, allocationType, autoAllocationConfirmed]);

  // Apply pending ambulance types when auto-allocation is confirmed
  useEffect(() => {
    if (autoAllocationConfirmed && allocationType === "Automatic" && pendingAmbulanceTypesRef.current.length > 0) {
      console.log("✅ [Parent] Auto-allocation confirmed - applying pending ambulance types:", pendingAmbulanceTypesRef.current.length);
      setAvailableAmbulanceTypes(pendingAmbulanceTypesRef.current);
    }
  }, [autoAllocationConfirmed, allocationType]);

  // State to control ChooseAmbulance card visibility
  const [showChooseAmbulanceCard, setShowChooseAmbulanceCard] = useState(false);

  // State for booking form data
  const [bookingFormData, setBookingFormData] = useState(null);

  // State for modals
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    data: null,
  });

  const [successModal, setSuccessModal] = useState({
    isOpen: false,
    data: null,
  });

  // Stop all automatic flows when successModal opens
  useEffect(() => {
    if (successModal.isOpen) {
      console.log('✅ [SuccessModal] Success modal opened - stopping all automatic flows');
      
      // 1. Stop automatic allocation timer
      if (automaticTimerRef.current) {
        clearInterval(automaticTimerRef.current);
        automaticTimerRef.current = null;
        automaticTimerStartTimeRef.current = null;
        console.log('🛑 [SuccessModal] Stopped automatic allocation timer');
      }
      
      // 2. Stop automatic allocation active state
      setAutomaticAllocationActive(false);
      setAutomaticSelectionConfirmed(true);
      setAutomaticCountdown(0);
      setCurrentPageIndex(0);
      console.log('🛑 [SuccessModal] Stopped automatic allocation active state');
      
      // 3. Close AutomaticAssignmentPopup (poll ambulance assigned modal)
      setAutomaticAssignmentPopup({ isOpen: false, ambulance: null, driver: null, timer: 30, bookingId: null });
      console.log('🛑 [SuccessModal] Closed AutomaticAssignmentPopup');
      
      // 4. Clear availableAmbulanceTypes (candidate list UI)
      setAvailableAmbulanceTypes([]);
      console.log('🛑 [SuccessModal] Cleared availableAmbulanceTypes (candidate list)');
      
      // 5. Reset autoAllocationConfirmed and allocationType to hide "Available Ambulances" modal
      setAutoAllocationConfirmed(false);
      setAllocationType("");
      console.log('🛑 [SuccessModal] Reset autoAllocationConfirmed and allocationType');
      
      // 6. Stop allocation polling
      if (allocationPollingIntervalRef.current) {
        clearInterval(allocationPollingIntervalRef.current);
        allocationPollingIntervalRef.current = null;
        setIsPollingAllocation(false);
        setAssigningCandidateId(null);
        console.log('🛑 [SuccessModal] Stopped allocation polling');
      }
      
      // 7. Close confirmation modal (to prevent it showing behind success modal)
      setConfirmationModal({ isOpen: false, data: null });
      console.log('🛑 [SuccessModal] Closed confirmation modal');
      
      // 8. Close loading modal (to prevent it showing behind success modal)
      setShowLoadingModal(false);
      console.log('🛑 [SuccessModal] Closed loading modal');
      
      // 9. Close all other modals that might be visible
      setValidationErrorModal({ isOpen: false, title: '', message: '' });
      setOpenOthersModal({ isOpen: false, preferredFleetName: null, context: null });
      console.log('🛑 [SuccessModal] Closed all other modals');
    }
  }, [successModal.isOpen]);

  // Open-to-others decision modal
  const [openOthersModal, setOpenOthersModal] = useState({
    isOpen: false,
    preferredFleetName: null,
    context: null,
  });

  // Payment modal state
  const [paymentModal, setPaymentModal] = useState({
    isOpen: false,
    bookingId: null,
    bookingData: null,
    estimatedFare: null,
    patientName: null,
    pickupAddress: null,
    destinationAddress: null,
    ambulanceType: null,
  });

  // Payment order creation modal state (for fleet users)
  const [paymentOrderModal, setPaymentOrderModal] = useState({
    isOpen: false,
    bookingId: null,
    bookingData: null,
    estimatedFare: null,
  });

  // Payment verification modal state
  const [verificationModal, setVerificationModal] = useState({
    isOpen: false,
    orderId: null,
    paymentId: null,
    signature: null,
    bookingId: null,
  });

  // Error modal state for validation errors
  const [validationErrorModal, setValidationErrorModal] = useState({
    isOpen: false,
    title: "Validation Error",
    message: "",
  });

  // State for loading and error
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showLoadingModal, setShowLoadingModal] = useState(false);

  // Monitor when both pickup and dropoff coordinates are available
  useEffect(() => {
    if (pickupCoordinates && dropCoordinates) {
      setShowChooseAmbulanceCard(true);
    } else {
      setShowChooseAmbulanceCard(false);
    }
  }, [pickupCoordinates, dropCoordinates]);

  // Automatic allocation countdown timer effect
  // IMPORTANT: Timer is managed ENTIRELY on UI side - always 30 seconds, never from backend
  // For fleet users: Timer restarts automatically after each iteration to continue auto-iteration
  // FIXED: Use more frequent interval checks (100ms) to prevent timer stopping intermittently on live servers
  useEffect(() => {
    console.log('⏱️ [TIMER EFFECT] Effect triggered with:', {
      automaticAllocationActive,
      allocationType,
      availableAmbulanceTypesLength: availableAmbulanceTypes.length,
      automaticSelectionConfirmed,
      currentAutomaticIndex,
      timerRestartTrigger
    });
    
    // Clear any existing timer first
    if (automaticTimerRef.current) {
      console.log('⏱️ [TIMER EFFECT] Clearing existing timer');
      clearInterval(automaticTimerRef.current);
      automaticTimerRef.current = null;
      automaticTimerStartTimeRef.current = null;
      lastDisplayedAutomaticCountdownRef.current = null;
    }

    // For fleet users in automatic mode: Continue iteration even if availableAmbulanceTypes is empty
    // The API will provide candidates, so we don't need to check availableAmbulanceTypes.length
    const shouldStartTimer = automaticAllocationActive && 
                            allocationType === "Automatic" && 
                            !automaticSelectionConfirmed &&
                            (isFleetUser() || availableAmbulanceTypes.length > 0);

    if (shouldStartTimer) {
      // UI-managed timer: Always reset to 30 seconds when starting timer for a new ambulance
      // Timer is NOT updated from backend - strictly UI-managed
      // Use timestamp-based approach for reliable countdown on live servers
      // For fleet users: Timer restarts after each iteration to continue auto-iteration
      const resetTimer = () => {
        console.log('⏱️ [TIMER] Starting UI-managed 30-second countdown timer for ambulance index:', currentAutomaticIndex);
        console.log('⏱️ [TIMER] Fleet user:', isFleetUser(), 'Allocation type:', allocationType);
        
        // UI-managed: Always reset to 30 seconds (never from backend)
        const TIMER_DURATION = 30; // 30 seconds
        
        // Store start time for timestamp-based countdown (more reliable on live servers)
        automaticTimerStartTimeRef.current = Date.now();
        lastDisplayedAutomaticCountdownRef.current = TIMER_DURATION;
        setAutomaticCountdown(TIMER_DURATION);
        
        // Start countdown timer using timestamp-based approach for reliable timing
        // FIXED: Use 100ms interval checks instead of 1000ms to prevent timer stopping intermittently
        // This ensures smooth updates even if browser throttles intervals or there are performance issues
        const updateTimer = () => {
          if (!automaticTimerStartTimeRef.current) {
            return;
          }
          
          const elapsed = Math.floor((Date.now() - automaticTimerStartTimeRef.current) / 1000);
          const remaining = Math.max(0, TIMER_DURATION - elapsed);
          
          // Only update state when the second value actually changes
          // This prevents unnecessary re-renders and ensures smooth updates
          if (remaining !== lastDisplayedAutomaticCountdownRef.current) {
            lastDisplayedAutomaticCountdownRef.current = remaining;
            setAutomaticCountdown(remaining);
          }
          
          if (remaining <= 0) {
            // UI-managed timer expired - trigger next driver API
            // Timer will be reset to 30 seconds after next driver is assigned
            // Clear the interval first
            if (automaticTimerRef.current) {
              clearInterval(automaticTimerRef.current);
              automaticTimerRef.current = null;
            }
            automaticTimerStartTimeRef.current = null;
            
            // Call async function to trigger next driver API
            // After next driver is assigned, timer will restart at 30 seconds
            // Use the latest handleAutomaticTimeout from the ref
            if (handleAutomaticTimeoutRef.current) {
              handleAutomaticTimeoutRef.current().catch(error => {
                console.error('❌ [TIMER] Error in handleAutomaticTimeout:', error);
              });
            }
          }
        };
        
        // Update immediately
        updateTimer();
        
        // FIXED: Use 100ms interval instead of 1000ms for more reliable updates on live servers
        // This prevents timer from stopping intermittently due to browser throttling
        // The timestamp-based calculation ensures accuracy even if intervals are delayed
        automaticTimerRef.current = setInterval(updateTimer, 100);
      };
      
      // Start timer immediately without delay
      resetTimer();
      
      return () => {
        if (automaticTimerRef.current) {
          console.log('⏱️ [TIMER EFFECT] Cleaning up timer in resetTimer cleanup');
          clearInterval(automaticTimerRef.current);
          automaticTimerRef.current = null;
        }
        automaticTimerStartTimeRef.current = null;
        lastDisplayedAutomaticCountdownRef.current = null;
      };
    } else {
      console.log('⏱️ [TIMER EFFECT] Conditions not met - timer not started:', {
        automaticAllocationActive,
        allocationType,
        hasAmbulances: availableAmbulanceTypes.length > 0,
        automaticSelectionConfirmed
      });
    }

    return () => {
      if (automaticTimerRef.current) {
        console.log('⏱️ [TIMER EFFECT] Cleaning up timer in main cleanup');
        clearInterval(automaticTimerRef.current);
        automaticTimerRef.current = null;
      }
      automaticTimerStartTimeRef.current = null;
      lastDisplayedAutomaticCountdownRef.current = null;
    };
  }, [automaticAllocationActive, allocationType, availableAmbulanceTypes.length, automaticSelectionConfirmed, currentAutomaticIndex, timerRestartTrigger]);

  // Pagination logic: Show 3 cards at a time, auto-scroll to next page when countdown moves
  useEffect(() => {
    if (automaticAllocationActive && allocationType === "Automatic" && availableAmbulanceTypes.length > 0 && !automaticSelectionConfirmed) {
      // Calculate which page (set of 3 cards) the current active index belongs to
      const cardsPerPage = 3;
      const newPageIndex = Math.floor(currentAutomaticIndex / cardsPerPage);
      
      // If we've moved to a new page, update the page index and scroll
      if (newPageIndex !== currentPageIndex) {
        console.log('📄 [PAGINATION] Moving to new page:', {
          currentIndex: currentAutomaticIndex,
          oldPage: currentPageIndex,
          newPage: newPageIndex,
          totalCards: availableAmbulanceTypes.length
        });
        
        setCurrentPageIndex(newPageIndex);
        
        // Scroll to the top of the container to show the new page of 3 cards
        if (automaticScrollContainerRef.current) {
          setTimeout(() => {
            if (automaticScrollContainerRef.current) {
              automaticScrollContainerRef.current.scrollTo({
                top: 0,
                behavior: 'smooth'
              });
              console.log('📜 [PAGINATION] Scrolled to top of new page');
            }
          }, 100);
        }
      }
    }
  }, [currentAutomaticIndex, automaticAllocationActive, allocationType, availableAmbulanceTypes.length, automaticSelectionConfirmed, currentPageIndex]);
  
  // Reset page index when automatic allocation starts
  useEffect(() => {
    if (automaticAllocationActive && allocationType === "Automatic" && availableAmbulanceTypes.length > 0) {
      setCurrentPageIndex(0);
      // Scroll to top when starting
      if (automaticScrollContainerRef.current) {
        setTimeout(() => {
          if (automaticScrollContainerRef.current) {
            automaticScrollContainerRef.current.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
          }
        }, 100);
      }
    }
  }, [automaticAllocationActive, allocationType, availableAmbulanceTypes.length]);

  // Show and update automatic assignment popup when automatic allocation is active
  useEffect(() => {
    // CRITICAL: Never show popup when there are no candidates/ambulances available
    if (availableAmbulanceTypes.length === 0) {
      // Close popup immediately if no candidates are available
      setAutomaticAssignmentPopup({ isOpen: false, ambulance: null, driver: null, timer: 30, bookingId: null });
      // Also stop automatic allocation if it's active
      if (automaticAllocationActive) {
        console.log('🛑 [POPUP] No candidates available - stopping automatic allocation');
        setAutomaticAllocationActive(false);
      }
      return;
    }
    
    if (automaticAllocationActive && allocationType === "Automatic" && availableAmbulanceTypes.length > 0 && !automaticSelectionConfirmed) {
      const currentAmbulance = availableAmbulanceTypes[currentAutomaticIndex];

      if (!currentAmbulance) {
        console.warn('⚠️ [POPUP] No ambulance data at index', currentAutomaticIndex, 'Array length:', availableAmbulanceTypes.length);
      }

      if (currentAmbulance) {
        // IMPORTANT: Use currentAmbulance data for CONSISTENCY with the Active card display
        // Do NOT use allocationStatus for driver/fare - it may be out of sync with currentAutomaticIndex
        // The Active card uses currentAmbulance.driver, so popup should too
        const realDriver = currentAmbulance?.driver ? {
          name: currentAmbulance.driver.name || 'N/A',
          phone: currentAmbulance.driver.phone || 'N/A',
          rating: currentAmbulance.driver.rating || null,
          ambulanceNumber: currentAmbulance.plate_number || currentAmbulance.vehicle_id || null
        } : null;

        // Get fare from currentAmbulance for CONSISTENCY with the Active card display
        // Cards use: ambulance?.estimated_fare || apiEstimate
        const apiEstimate = fareEstimates[currentAmbulance?.type_id];
        const realEstimatedFare = currentAmbulance?.estimated_fare || apiEstimate || null;

        const realAmbulanceData = {
          ...currentAmbulance,
          price: realEstimatedFare,
          estimated_fare: realEstimatedFare
        };

        // Show or update popup with current ambulance and timer
        // All data comes from currentAmbulance to stay in sync with Active card
        setAutomaticAssignmentPopup(prev => ({
          ...prev,
          isOpen: true,
          ambulance: realAmbulanceData,
          driver: realDriver,
          timer: automaticCountdown,
          bookingId: currentBookingId || prev.bookingId || currentAmbulance?.booking_id || null
        }));

        // Update selected driver with data from currentAmbulance
        if (realDriver) {
          setAutomaticSelectedDriver(realDriver);
        }
      }
    } else if (!automaticAllocationActive || automaticSelectionConfirmed || availableAmbulanceTypes.length === 0) {
      // Close popup when automatic allocation stops, is confirmed, or when there are no candidates
      setAutomaticAssignmentPopup({ isOpen: false, ambulance: null, driver: null, timer: 30, bookingId: null });
    }
  }, [automaticAllocationActive, allocationType, availableAmbulanceTypes, currentAutomaticIndex, automaticCountdown, automaticSelectionConfirmed, fareEstimates, currentBookingId, allocationStatus]);

  // Force modal update when currentAutomaticIndex changes to ensure it shows the correct driver
  // This ensures modal updates immediately when index changes, reading latest data from availableAmbulanceTypes
  // CRITICAL: This effect must run AFTER availableAmbulanceTypes is updated to show correct driver
  useEffect(() => {
    if (automaticAllocationActive && allocationType === "Automatic" && availableAmbulanceTypes.length > 0 && !automaticSelectionConfirmed) {
      const currentAmbulance = availableAmbulanceTypes[currentAutomaticIndex];
      
      if (currentAmbulance) {
        const driverName = currentAmbulance.driver?.name || 'N/A';
        const driverId = currentAmbulance.driver?.driver_id || currentAmbulance.driver_id || 'N/A';
        console.log('🔄 [MODAL UPDATE] Index:', currentAutomaticIndex, 'Driver:', driverName, 'Driver ID:', driverId, 'Array length:', availableAmbulanceTypes.length);
        
        // Get driver data from current ambulance - always read fresh from array
        // This MUST match the active card's driver to keep modal in sync
        const realDriver = currentAmbulance?.driver ? {
          name: currentAmbulance.driver.name || 'N/A',
          phone: currentAmbulance.driver.phone || 'N/A',
          rating: currentAmbulance.driver.rating || null,
          driver_id: currentAmbulance.driver.driver_id || currentAmbulance.driver_id || null,
          ambulanceNumber: currentAmbulance.plate_number || currentAmbulance.vehicle_id || null
        } : null;

        // Get fare from currentAmbulance
        const apiEstimate = fareEstimates[currentAmbulance?.type_id];
        const realEstimatedFare = currentAmbulance?.estimated_fare || apiEstimate || null;

        const realAmbulanceData = {
          ...currentAmbulance,
          price: realEstimatedFare,
          estimated_fare: realEstimatedFare
        };

        // Force update modal with current candidate data - this ensures modal shows correct driver
        // CRITICAL: This must use realDriver from currentAmbulance to match active card
        setAutomaticAssignmentPopup(prev => {
          const newPopup = {
            ...prev,
            isOpen: true,
            ambulance: realAmbulanceData,
            driver: realDriver, // Use driver from currentAmbulance, not allocationStatus
            timer: automaticCountdown,
            bookingId: currentBookingId || prev.bookingId || currentAmbulance?.booking_id || null
          };
          
          console.log('✅ [MODAL UPDATE] Modal updated with driver:', realDriver?.name, 'Driver ID:', realDriver?.driver_id);
          return newPopup;
        });

        // Update selected driver
        if (realDriver) {
          setAutomaticSelectedDriver(realDriver);
        }
      } else {
        console.warn('⚠️ [MODAL UPDATE] No ambulance data at index', currentAutomaticIndex, 'Array length:', availableAmbulanceTypes.length);
      }
    }
  }, [currentAutomaticIndex, automaticAllocationActive, allocationType, availableAmbulanceTypes, automaticSelectionConfirmed, fareEstimates, currentBookingId, automaticCountdown]);

  // State to track if auto-assignment should start (after user confirms)
  const [shouldStartAutoAssignment, setShouldStartAutoAssignment] = useState(false);

  // Reset automatic allocation when allocation type changes
  useEffect(() => {
    if (allocationType !== "Automatic") {
      setAutomaticAllocationActive(false);
      setCurrentAutomaticIndex(0);
      setCurrentPageIndex(0);
      setAutomaticCountdown(30);
      setAutomaticSelectedDriver(null);
      setAutomaticSelectionConfirmed(false);
      setShouldStartAutoAssignment(false);
      setAutoAllocationConfirmed(false); // Reset confirmation state
      // Clear available ambulance types only when switching away from Automatic
      setAvailableAmbulanceTypes([]);
      pendingAmbulanceTypesRef.current = [];
      if (automaticTimerRef.current) {
        clearInterval(automaticTimerRef.current);
        automaticTimerRef.current = null;
        automaticTimerStartTimeRef.current = null;
      }
    } else if (allocationType === "Automatic" && availableAmbulanceTypes.length > 0 && shouldStartAutoAssignment && !automaticAllocationActive) {
      // Start automatic allocation only when user confirms (shouldStartAutoAssignment is true)
      // CRITICAL: Only start if there are available ambulances
      console.log('🔄 [AUTO-ASSIGN] Starting automatic allocation after user confirmation');
      setAutomaticAllocationActive(true);
      setCurrentAutomaticIndex(0);
      setCurrentPageIndex(0);
      setAutomaticCountdown(30);
      setAutomaticSelectionConfirmed(false);
      setAutomaticSelectedDriver(null);
    } else if (allocationType === "Automatic" && availableAmbulanceTypes.length === 0 && automaticAllocationActive) {
      // CRITICAL: Stop automatic allocation if candidates become empty
      console.log('🛑 [AUTO-ASSIGN] No candidates available - stopping automatic allocation');
      setAutomaticAllocationActive(false);
      setAutomaticAssignmentPopup({ isOpen: false, ambulance: null, driver: null, timer: 30, bookingId: null });
    }
    
    // Clear candidates when switching away from manual mode
    if (allocationType !== "Manual") {
      setCandidates([]);
      setCandidatesError(null);
      setHasSearchedCandidates(false); // Reset search flag when switching modes
    }
  }, [allocationType, availableAmbulanceTypes.length, shouldStartAutoAssignment]);

  // Handle automatic allocation timeout - trigger next driver API
  // Flow: getCandidates → startAutoAllocation (picks TOP 1) → triggerNextDriver (picks NEXT from same candidate list)
  // Note: Stored in ref so timer effect can access it without dependency issues
  const handleAutomaticTimeout = useCallback(async () => {
    console.log('⏰ [AUTO-SELECTION FLOW] Automatic allocation timeout - triggering next driver API');
    console.log('⏰ [AUTO-SELECTION FLOW] Current index:', currentAutomaticIndex);
    console.log('⏰ [AUTO-SELECTION FLOW] Available ambulances:', availableAmbulanceTypes.length);
    console.log('⏰ [AUTO-SELECTION FLOW] Selection confirmed:', automaticSelectionConfirmed);
    
    // Check if we have a bookingId to call the API
    if (!currentBookingId) {
      console.error('❌ [AUTO-SELECTION FLOW] No bookingId available - cannot trigger next driver API');
      // Fallback to old logic if no bookingId
      const currentAmbulance = availableAmbulanceTypes[currentAutomaticIndex];
      const nextIndex = currentAutomaticIndex + 1;
      
      if (nextIndex < availableAmbulanceTypes.length) {
        console.log('🔄 [AUTO-SELECTION FLOW] Moving to next card (no bookingId) at index:', nextIndex);
        // Update index - timer effect will automatically restart when index changes
        setCurrentAutomaticIndex(nextIndex);
      } else if (currentAmbulance && !automaticSelectionConfirmed) {
        handleAutomaticAmbulanceSelect(currentAmbulance);
      } else {
        setAutomaticAllocationActive(false);
      }
      return;
    }
    
    try {
      // Validate bookingId before making API call
      if (!currentBookingId || (typeof currentBookingId !== 'string' && typeof currentBookingId !== 'number')) {
        console.error('❌ [AUTO-SELECTION FLOW] Invalid bookingId:', currentBookingId);
        throw new Error('Invalid booking ID - cannot trigger next driver');
      }
      
      // Prevent rapid successive calls (minimum 2 seconds between calls)
      const now = Date.now();
      if (lastTriggerNextDriverCallRef.current && (now - lastTriggerNextDriverCallRef.current) < 2000) {
        const waitTime = 2000 - (now - lastTriggerNextDriverCallRef.current);
        console.log(`⏳ [AUTO-SELECTION FLOW] Rate limiting: waiting ${waitTime}ms before next call`);
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }
      lastTriggerNextDriverCallRef.current = Date.now();
      
      // For fleet users: Verify booking is in a valid state before calling triggerNextDriver
      // This prevents calling the API when the booking is not ready
      if (isFleetUser() && allocationType === "Automatic") {
        try {
          console.log('🔍 [FLEET AUTO-ITERATION] Verifying booking state before triggering next driver');
          const statusCheck = await authService.fleetGetAllocationStatus(currentBookingId);
          
          if (!statusCheck.success || !statusCheck.data) {
            console.warn('⚠️ [FLEET AUTO-ITERATION] Booking status check failed - booking may not be ready');
            // Continue anyway - the triggerNextDriver API will handle the error
          } else {
            const bookingState = statusCheck.data.ui_state || statusCheck.data.driver_response?.status;
            console.log('✅ [FLEET AUTO-ITERATION] Booking state verified:', bookingState);
            
            // If booking is already accepted or completed, don't call triggerNextDriver
            if (bookingState === 'ACCEPTED' || bookingState === 'COMPLETED') {
              console.log('✅ [FLEET AUTO-ITERATION] Driver already accepted - stopping auto-iteration');
              setAutomaticAllocationActive(false);
              if (automaticTimerRef.current) {
                clearInterval(automaticTimerRef.current);
                automaticTimerRef.current = null;
              }
              return;
            }
          }
        } catch (statusError) {
          console.warn('⚠️ [FLEET AUTO-ITERATION] Error checking booking status, continuing with triggerNextDriver:', statusError);
          // Continue with triggerNextDriver even if status check fails
        }
      }
      
      // Call trigger next driver API with retry logic for timeout errors
      // Backend should use the SAME candidate list from startAutoAllocation
      // and pick the NEXT driver from that list (excluding already tried ones)
      console.log('🔄 [AUTO-SELECTION FLOW] Calling trigger next driver API for booking:', currentBookingId);
      console.log('🔄 [AUTO-SELECTION FLOW] Backend should pick NEXT driver from the original candidate list');
      
      // Retry logic for timeout errors (max 2 retries)
      let result;
      let lastError;
      const maxRetries = 2;
      
      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          if (attempt > 0) {
            console.log(`🔄 [AUTO-SELECTION FLOW] Retry attempt ${attempt} of ${maxRetries} for triggerNextDriver`);
            // Exponential backoff: wait 1s, 2s, 4s
            await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt - 1) * 1000));
          }
          
          // Use fleet endpoint for fleet users, hospital endpoint for hospital users
          result = isFleetUser()
            ? await authService.fleetTriggerNextDriver(currentBookingId)
            : await hospitalService.triggerNextDriver(currentBookingId);
          
          // If we get a successful response, break out of retry loop
          if (result && result.success && result.data) {
            break;
          }
          
          // If response indicates failure but not timeout, throw error
          if (result && !result.success) {
            const errorMsg = result.message || 'Failed to trigger next driver';
            // Only retry on timeout errors
            if (errorMsg.toLowerCase().includes('timeout') && attempt < maxRetries) {
              lastError = new Error(errorMsg);
              continue; // Retry
            }
            throw new Error(errorMsg);
          }
        } catch (error) {
          lastError = error;
          const errorMessage = error?.response?.data?.message || 
                             error?.response?.data?.error || 
                             error?.message || 
                             'Failed to trigger next driver';
          
          // Check if it's a timeout error
          const isTimeoutError = errorMessage.toLowerCase().includes('timeout') ||
                                error?.response?.status === 500 ||
                                error?.code === 'ECONNABORTED';
          
          if (isTimeoutError && attempt < maxRetries) {
            console.warn(`⚠️ [AUTO-SELECTION FLOW] Timeout error on attempt ${attempt + 1}, will retry...`);
            continue; // Retry
          }
          
          // If not a timeout or max retries reached, throw the error
          throw error;
        }
      }
      
      // If we exhausted retries, throw the last error
      if (!result || !result.success || !result.data) {
        throw lastError || new Error('Failed to trigger next driver after retries');
      }
      
      const responseData = result.data;
      console.log('✅ [AUTO-SELECTION FLOW] Next driver API response:', responseData);
      
      // Check the status from API response
      if (responseData.status === 'FAILED_NO_DRIVER') {
        // No more drivers available from the candidate list - stop automatic allocation
        console.log('⚠️ [AUTO-SELECTION FLOW] No more drivers available from candidate list - stopping allocation');
        console.log('📝 [AUTO-SELECTION FLOW] Message:', responseData.message);
        setAutomaticAllocationActive(false);
        
        // Optionally show a message to user
        if (responseData.message) {
          // You can add a notification here if needed
          console.log('💬 [AUTO-SELECTION FLOW] User message:', responseData.message);
        }
      } else {
        // Next driver triggered successfully from candidate list - immediately poll for new driver data
        console.log('✅ [AUTO-SELECTION FLOW] Next driver triggered successfully from candidate list');
        
        // Immediately poll allocation status to get new driver data
        // This ensures UI updates quickly with new driver info
        try {
          // Use fleet endpoint for fleet users, hospital endpoint for hospital users
          const pollResponse = isFleetUser()
            ? await authService.fleetGetAllocationStatus(currentBookingId)
            : await hospitalService.getAllocationStatus(currentBookingId);
          if (pollResponse.success && pollResponse.data) {
            console.log('📥 [AUTO-SELECTION FLOW] New driver data from immediate poll:', pollResponse.data);
            
            // Normalize response structure for fleet API
            const normalizedPollData = isFleetUser() ? {
              ...pollResponse.data,
              response_id: pollResponse.data.driver_response?.response_id || pollResponse.data.response_id,
              driver_response: {
                ...pollResponse.data.driver_response,
                response_id: pollResponse.data.driver_response?.response_id || pollResponse.data.response_id,
                status: pollResponse.data.driver_response?.status || pollResponse.data.ui_state,
                expires_in_sec: pollResponse.data.driver_response?.expires_in_sec,
                responded_at: pollResponse.data.driver_response?.responded_at
              }
            } : pollResponse.data;
            
            // Update allocation status with new driver data (real-time update)
            setAllocationStatus(prevStatus => ({
              ...prevStatus,
              ...normalizedPollData,
              // Use latest driver and vehicle data from API (real-time updates)
              driver: normalizedPollData.driver || prevStatus?.driver,
              vehicle: normalizedPollData.vehicle || prevStatus?.vehicle,
              estimated_fare: normalizedPollData.estimated_fare || prevStatus?.estimated_fare,
              allocation_type: normalizedPollData.allocation_type || 'AUTOMATIC',
              // Ensure response_id is available at top level
              response_id: normalizedPollData.response_id || normalizedPollData.driver_response?.response_id || prevStatus?.response_id
            }));
            
            // Update the NEXT candidate's data with new driver information from API
            // This ensures when we move to next candidate, it has the correct data
            // Works for both fleet and hospital users
            if (allocationType === "Automatic" && normalizedPollData.driver && normalizedPollData.vehicle) {
              const nextIndex = currentAutomaticIndex + 1;
              console.log('🔄 [AUTO-SELECTION FLOW] Updating next candidate (index ' + nextIndex + ') with new driver data from API');
              
              // Update the next candidate in availableAmbulanceTypes with new driver/vehicle data
              setAvailableAmbulanceTypes(prevTypes => {
                if (prevTypes.length === 0) {
                  console.warn('⚠️ [AUTO-SELECTION FLOW] Cannot update card - empty array');
                  return prevTypes;
                }
                
                const updatedTypes = [...prevTypes];
                
                // Ensure the array is long enough - extend if needed
                while (nextIndex >= updatedTypes.length) {
                  // If next index doesn't exist, use the last available card as template
                  const templateCard = updatedTypes[updatedTypes.length - 1];
                  if (templateCard) {
                    updatedTypes.push({ ...templateCard });
                  } else {
                    console.warn('⚠️ [AUTO-SELECTION FLOW] Cannot extend array - no template card');
                    return prevTypes;
                  }
                }
                
                // Get the base card data at next index
                const baseCard = updatedTypes[nextIndex];
                
                if (baseCard) {
                  // Update the card at next index with new driver and vehicle data from API
                  updatedTypes[nextIndex] = {
                    ...baseCard,
                    // Update driver information from API
                    driver: {
                      name: normalizedPollData.driver.name || baseCard.driver?.name || 'N/A',
                      phone: normalizedPollData.driver.phone || baseCard.driver?.phone || 'N/A',
                      rating: normalizedPollData.driver.rating || baseCard.driver?.rating || null,
                      driver_id: normalizedPollData.driver.id || normalizedPollData.driver.driver_id || baseCard.driver?.driver_id
                    },
                    // Update vehicle information from API
                    plate_number: normalizedPollData.vehicle.number || normalizedPollData.vehicle.plate_number || baseCard.plate_number,
                    vehicle_number: normalizedPollData.vehicle.number || normalizedPollData.vehicle.vehicle_number || baseCard.vehicle_number,
                    vehicle_id: normalizedPollData.vehicle.id || normalizedPollData.vehicle.vehicle_id || baseCard.vehicle_id,
                    // Update estimated fare if available
                    estimated_fare: normalizedPollData.estimated_fare || baseCard.estimated_fare
                  };
                  
                  console.log('✅ [AUTO-SELECTION FLOW] Next candidate updated with new driver:', {
                    driverName: updatedTypes[nextIndex].driver?.name,
                    vehicleNumber: updatedTypes[nextIndex].plate_number,
                    nextIndex: nextIndex,
                    arrayLength: updatedTypes.length
                  });
                } else {
                  console.warn('⚠️ [AUTO-SELECTION FLOW] Cannot update card - no base card found at index', nextIndex);
                }
                
                return updatedTypes;
              });
            }
            
            // For fleet users: Check if driver has accepted - if yes, stop auto-iteration
            if (isFleetUser() && allocationType === "Automatic") {
              const driverAccepted = normalizedPollData.driver_response?.status === 'ACCEPTED' || 
                                    normalizedPollData.ui_state === 'ACCEPTED';
              
              if (driverAccepted) {
                console.log('✅ [FLEET AUTO-ITERATION] Driver accepted - stopping auto-iteration');
                setAutomaticAllocationActive(false);
                // Stop timer
                if (automaticTimerRef.current) {
                  clearInterval(automaticTimerRef.current);
                  automaticTimerRef.current = null;
                }
                automaticTimerStartTimeRef.current = null;
                // Stop polling
                if (allocationPollingIntervalRef.current) {
                  clearInterval(allocationPollingIntervalRef.current);
                  allocationPollingIntervalRef.current = null;
                }
                return; // Stop here - driver has accepted
              }
            }
            
            // UI-managed timer: Always reset to 30 seconds for new driver cycle
            // Timer is managed entirely on UI side, not from backend
            // When next driver API is called, move to next candidate and restart timer
            // The timer will expire again and trigger next driver API
            console.log('⏱️ [AUTO-SELECTION FLOW] Restarting 30-second timer for next iteration');
            
            // Move to next candidate index for both fleet and hospital users
            // This ensures timer moves to next candidate and "Next" chip moves to candidate after that
            const nextIndex = currentAutomaticIndex + 1;
            if (nextIndex < availableAmbulanceTypes.length) {
              console.log('🔄 [AUTO-SELECTION FLOW] Moving to next candidate at index:', nextIndex);
              console.log('🔄 [AUTO-SELECTION FLOW] Timer will restart on next candidate, "Next" chip will move to candidate after that');
              
              // Clear existing timer first
              if (automaticTimerRef.current) {
                clearInterval(automaticTimerRef.current);
                automaticTimerRef.current = null;
              }
              automaticTimerStartTimeRef.current = null;
              lastDisplayedAutomaticCountdownRef.current = null;
              
              // Move to next candidate - timer effect will automatically restart when index changes
              // Increment index first, then the modal effect will run and read the updated array
              setCurrentAutomaticIndex(nextIndex);
              setAutomaticCountdown(30);
            } else {
              console.log('⚠️ [AUTO-SELECTION FLOW] No more candidates available - stopping automatic allocation');
              setAutomaticAllocationActive(false);
            }
          }
        } catch (pollError) {
          console.error('❌ [AUTO-SELECTION FLOW] Error polling after trigger next driver:', pollError);
          
          // Continue iteration by moving to next candidate even on poll error
          // This ensures timer and "Next" chip move correctly
          const nextIndex = currentAutomaticIndex + 1;
          if (nextIndex < availableAmbulanceTypes.length) {
            console.log('🔄 [AUTO-SELECTION FLOW] Poll error - moving to next candidate at index:', nextIndex);
            
            // Clear existing timer first
            if (automaticTimerRef.current) {
              clearInterval(automaticTimerRef.current);
              automaticTimerRef.current = null;
            }
            automaticTimerStartTimeRef.current = null;
            lastDisplayedAutomaticCountdownRef.current = null;
            
            // Move to next candidate - timer effect will automatically restart when index changes
            setCurrentAutomaticIndex(nextIndex);
            setAutomaticCountdown(30);
          } else {
            console.log('⚠️ [AUTO-SELECTION FLOW] No more candidates available after poll error');
            setAutomaticAllocationActive(false);
          }
        }
      }
    } catch (error) {
      console.error('❌ [AUTO-SELECTION FLOW] Error triggering next driver:', error);
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data?.error || 
                          error?.message || 
                          'Failed to trigger next driver';
      
      // For fleet users: Handle timeout errors more gracefully
      if (isFleetUser() && allocationType === "Automatic") {
        const isTimeoutError = errorMessage.toLowerCase().includes('timeout') ||
                              error?.response?.status === 500 ||
                              error?.code === 'ECONNABORTED';
        
        if (isTimeoutError) {
          console.warn('⚠️ [FLEET AUTO-ITERATION] Timeout error - will retry on next timer cycle');
          // Don't stop the iteration - let it retry on the next timer cycle
          // Reset timer to continue trying
          if (automaticTimerRef.current) {
            clearInterval(automaticTimerRef.current);
            automaticTimerRef.current = null;
          }
          automaticTimerStartTimeRef.current = null;
          lastDisplayedAutomaticCountdownRef.current = null;
          
          // Restart timer to retry after 30 seconds
          const TIMER_DURATION = 30;
          automaticTimerStartTimeRef.current = Date.now();
          lastDisplayedAutomaticCountdownRef.current = TIMER_DURATION;
          setAutomaticCountdown(TIMER_DURATION);
          
          const updateTimer = () => {
            if (!automaticTimerStartTimeRef.current) {
              return;
            }
            
            const elapsed = Math.floor((Date.now() - automaticTimerStartTimeRef.current) / 1000);
            const remaining = Math.max(0, TIMER_DURATION - elapsed);
            
            if (remaining !== lastDisplayedAutomaticCountdownRef.current) {
              lastDisplayedAutomaticCountdownRef.current = remaining;
              setAutomaticCountdown(remaining);
            }
            
            if (remaining <= 0) {
              if (automaticTimerRef.current) {
                clearInterval(automaticTimerRef.current);
                automaticTimerRef.current = null;
              }
              automaticTimerStartTimeRef.current = null;
              
              // Retry triggerNextDriver
              if (handleAutomaticTimeoutRef.current) {
                handleAutomaticTimeoutRef.current().catch(err => {
                  console.error('❌ [FLEET AUTO-ITERATION] Retry error:', err);
                });
              }
            }
          };
          
          updateTimer();
          // FIXED: Use 100ms interval instead of 1000ms for more reliable updates on live servers
          automaticTimerRef.current = setInterval(updateTimer, 100);
          
          // Show user-friendly error message
          setError('Connection timeout - retrying automatically. Please wait...');
          
          return; // Exit early - timer will retry
        }
      }
      
      // For other errors or non-fleet users: Fallback to old logic
      console.log('⚠️ [AUTO-SELECTION FLOW] Falling back to local logic due to error');
      setError(errorMessage);
      
      const currentAmbulance = availableAmbulanceTypes[currentAutomaticIndex];
      const nextIndex = currentAutomaticIndex + 1;
      
      if (nextIndex < availableAmbulanceTypes.length) {
        console.log('🔄 [AUTO-SELECTION FLOW] Moving to next card (error fallback) at index:', nextIndex);
        // Update index - timer effect will automatically restart when index changes
        setCurrentAutomaticIndex(nextIndex);
      } else if (currentAmbulance && !automaticSelectionConfirmed) {
        handleAutomaticAmbulanceSelect(currentAmbulance);
      } else {
        setAutomaticAllocationActive(false);
      }
    }
  }, [currentAutomaticIndex, availableAmbulanceTypes, automaticSelectionConfirmed, currentBookingId]);

  // Update ref with the latest handleAutomaticTimeout function
  // This runs after handleAutomaticTimeout is defined, so no initialization error
  handleAutomaticTimeoutRef.current = handleAutomaticTimeout;

  // Handle rejection of ambulance during automatic allocation
  const handleRejectAmbulance = () => {
    console.log('❌ [AUTO-SELECTION FLOW] Ambulance rejected by user');
    console.log('❌ [AUTO-SELECTION FLOW] Current index:', currentAutomaticIndex);
    
    // Stop current timer
    if (automaticTimerRef.current) {
      clearInterval(automaticTimerRef.current);
      automaticTimerRef.current = null;
      automaticTimerStartTimeRef.current = null;
    }
    
    // Move to next ambulance
    const nextIndex = currentAutomaticIndex + 1;
    if (nextIndex < availableAmbulanceTypes.length) {
      console.log('🔄 [AUTO-SELECTION FLOW] Moving to next ambulance after rejection');
      console.log('🔄 [AUTO-SELECTION FLOW] Moving from index', currentAutomaticIndex, 'to index', nextIndex);
      console.log('🔄 [AUTO-SELECTION FLOW] Timer effect will start new 30-second timer for next ambulance');
      // Update index - timer effect will automatically restart when index changes
      setCurrentAutomaticIndex(nextIndex);
      
      // Auto-select if this is the last ambulance
      if (nextIndex === availableAmbulanceTypes.length - 1) {
        console.log('✅ [AUTO-SELECTION FLOW] This is the last ambulance - will auto-select after timer');
      }
    } else {
      // No more ambulances
      console.log('⚠️ [AUTO-SELECTION FLOW] No more ambulances available after rejection');
      setAutomaticAllocationActive(false);
    }
  };

  // Handle manual or automatic selection of ambulance during automatic allocation
  const handleAutomaticAmbulanceSelect = (ambulance) => {
    console.log('✅ [AUTO-SELECTION FLOW] Ambulance selected during automatic allocation');
    console.log('✅ [AUTO-SELECTION FLOW] Selected ambulance:', ambulance.name || ambulance.type);
    console.log('✅ [AUTO-SELECTION FLOW] Current index:', currentAutomaticIndex);
    console.log('✅ [AUTO-SELECTION FLOW] Total available ambulances:', availableAmbulanceTypes.length);
    
    // Stop the timer and confirm selection
    if (automaticTimerRef.current) {
      clearInterval(automaticTimerRef.current);
      automaticTimerRef.current = null;
      automaticTimerStartTimeRef.current = null;
      console.log('✅ [AUTO-SELECTION FLOW] Timer cleared');
    }
    
    setSelectedAmbulance(ambulance);
    setAutomaticSelectionConfirmed(true);
    setAutomaticAllocationActive(false);
    
    console.log('✅ [AUTO-SELECTION FLOW] Automatic allocation STOPPED - will NOT proceed to next ambulance');
    
    // Use real-time driver data from allocationStatus API response
    const assignedDriver = allocationStatus?.driver ? {
      name: allocationStatus.driver.name || 'N/A',
      phone: allocationStatus.driver.phone || 'N/A',
      rating: allocationStatus.driver.rating || null,
      ambulanceNumber: allocationStatus.vehicle?.number || null
    } : null;
    
    if (assignedDriver) {
      setAutomaticSelectedDriver(assignedDriver);
      console.log('✅ [AUTO-SELECTION FLOW] Driver assigned (from API):', assignedDriver.name);
      console.log('✅ [AUTO-SELECTION FLOW] Driver phone:', assignedDriver.phone);
    } else {
      console.warn('⚠️ [AUTO-SELECTION FLOW] No driver data available from allocationStatus');
    }
    
    // Close popup
    setAutomaticAssignmentPopup({ isOpen: false, ambulance: null, driver: null, timer: 30 });
    
    // Proceed with confirmation modal
    if (bookingFormData) {
      const updatedFormData = {
        ...bookingFormData,
        allocationType: "Automatic",
        automaticAllocation: {
          driver: assignedDriver,
          ambulance: ambulance,
        },
      };
      
      console.log('✅ [AUTO-SELECTION FLOW] Opening confirmation modal now');
      handleConfirmSelection(ambulance, updatedFormData);
    } else {
      console.warn('⚠️ [AUTO-SELECTION FLOW] No booking form data - cannot open confirmation modal');
    }
  };
  
  // Handle assign button in automatic assignment popup
  const handleAutomaticAssign = () => {
    console.log('✅ [AUTO-SELECTION FLOW] Assign button clicked in popup');
    
    // Get current ambulance from the popup or from current index
    const ambulanceToAssign = automaticAssignmentPopup.ambulance || availableAmbulanceTypes[currentAutomaticIndex];
    
    if (!ambulanceToAssign) {
      console.warn('⚠️ [AUTO-SELECTION FLOW] No ambulance to assign');
      return;
    }
    
    // Set as selected and confirm
    setSelectedAmbulance(ambulanceToAssign);
    setAutomaticSelectionConfirmed(true);
    setAutomaticAllocationActive(false);
    
    // Clear timer
    if (automaticTimerRef.current) {
      clearInterval(automaticTimerRef.current);
      automaticTimerRef.current = null;
      automaticTimerStartTimeRef.current = null;
    }
    
    // Close popup
    setAutomaticAssignmentPopup({ isOpen: false, ambulance: null, driver: null, timer: 30 });
    
    // Proceed with confirmation modal
    if (bookingFormData) {
      const updatedFormData = {
        ...bookingFormData,
        allocationType: "Automatic",
        automaticAllocation: {
          driver: automaticSelectedDriver,
          ambulance: ambulanceToAssign,
        },
      };
      
      console.log('✅ [AUTO-SELECTION FLOW] Opening confirmation modal now');
      handleConfirmSelection(ambulanceToAssign, updatedFormData);
    } else {
      console.warn('⚠️ [AUTO-SELECTION FLOW] No booking form data - cannot open confirmation modal');
    }
  };
  
  // Handle reject button in automatic assignment popup
  const handleAutomaticReject = () => {
    console.log('❌ [AUTO-SELECTION FLOW] Reject button clicked in popup');
    
    // Reset selection and continue automatic allocation
    setSelectedAmbulance(null);
    setAutomaticSelectionConfirmed(false);
    setAutomaticSelectedDriver(null);
    
    // Move to next ambulance (popup will update automatically via useEffect)
    handleRejectAmbulance();
  };
  
  // Handle close button in automatic assignment popup
  const handleAutomaticPopupClose = () => {
    console.log('❌ [AUTO-SELECTION FLOW] Popup closed - stopping automatic allocation');
    
    // Stop automatic allocation when user closes popup
    setAutomaticAllocationActive(false);
    setSelectedAmbulance(null);
    setAutomaticSelectionConfirmed(false);
    setAutomaticSelectedDriver(null);
    
    // Reset allocation type to show both options again
    setAllocationType("");
    setAutoAllocationConfirmed(false);
    setShouldStartAutoAssignment(false);
    setCurrentAutomaticIndex(0);
    setCurrentPageIndex(0);
    setAutomaticCountdown(30);
    
    // Clear booking and allocation status to allow fresh start
    setCurrentBookingId(null);
    setAllocationStatus(null);
    setIsPollingAllocation(false);
    setAssigningCandidateId(null);
    
    // Clear available ambulance types
    setAvailableAmbulanceTypes([]);
    pendingAmbulanceTypesRef.current = [];
    
    // Clear all timers and polling intervals
    if (automaticTimerRef.current) {
      clearInterval(automaticTimerRef.current);
      automaticTimerRef.current = null;
      automaticTimerStartTimeRef.current = null;
    }
    if (allocationPollingIntervalRef.current) {
      clearInterval(allocationPollingIntervalRef.current);
      allocationPollingIntervalRef.current = null;
    }
    
    // Close popup
    setAutomaticAssignmentPopup({ isOpen: false, ambulance: null, driver: null, timer: 30 });
    
    // Increment reset counter to force child component to reset its internal state
    setAllocationResetCounter(prev => prev + 1);
    
    console.log('✅ [AUTO-SELECTION FLOW] All states reset - user can now select allocation type again');
  };

  // Handle form data from GetAmbulanceCard
  const handleFormData = (formData) => {
    console.log("📝 Form data received in parent:", {
      hasPhone: !!formData.phone,
      hasEmail: !!formData.email,
      phone: formData.phone || "(empty)",
      email: formData.email || "(empty)",
      fullFormData: formData
    });
    setBookingFormData(formData);
  };

  // Handle ambulance selection
  const handleAmbulanceSelect = (ambulance) => {
    console.log("=== AMBULANCE SELECTION IN PARENT ===");
    console.log("Received ambulance:", ambulance);
    setSelectedAmbulance(ambulance);
    onBookingChange({ selectedAmbulance: ambulance });
  };

  // Handle confirm selection - show confirmation modal
  const handleConfirmSelection = (ambulance, formData = null) => {
    // Use passed formData or fallback to state
    const currentFormData = formData || bookingFormData;

    if (!currentFormData) {
      setError("Please fill in the booking form first");
      return;
    }

    if (!ambulance) {
      setError("Please select an ambulance first");
      return;
    }

    // Update bookingFormData state with the latest formData (including paymentType from ChooseAmbulance card)
    if (formData) {
      setBookingFormData(formData);
    }

    // Set the selected ambulance in state so it's available in handleConfirmBooking
    setSelectedAmbulance(ambulance);

    // Check if manual allocation was selected - if so, directly create booking
    if (currentFormData.allocationType === "Manual" && currentFormData.manualAllocation) {
      // Close confirmation modal if it's open (to prevent it showing behind success modal)
      setConfirmationModal({ isOpen: false, data: null });
      handleConfirmBookingForManual(currentFormData, ambulance);
      return;
    }

    // ❌ REMOVED: Frontend fare calculation - Only use backend fare estimates
    const estimatedFare = (ambulance?.type_id && fareEstimates[ambulance.type_id]) || null;

    // Prepare confirmation modal data
    const confirmationData = {
      patientName: currentFormData.name,
      phoneNumber: currentFormData.phone,
      pickupAddress: currentFormData.pickupLocation,
      destinationAddress: currentFormData.dropLocation,
      ambulanceType: ambulance?.name || "Ambulance",
      estimatedFare: estimatedFare,
      paymentType: currentFormData.paymentType,
      bookingType: currentFormData.bookingType,
      scheduledDateTime:
        currentFormData.bookingType === "scheduled" &&
        currentFormData.date &&
        currentFormData.time
          ? new Date(
              `${currentFormData.date}T${currentFormData.time}`
            ).toISOString()
          : undefined,
      direction: currentFormData.direction,
      pickupCoordinates: pickupCoordinates,
      dropCoordinates: dropCoordinates,
    };

    setConfirmationModal({
      isOpen: true,
      data: confirmationData,
    });
  };

  // Check for payment redirect on page load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('paymentStatus');
    const bookingId = urlParams.get('bookingId');
    const paymentId = urlParams.get('paymentId');

    if (paymentStatus === 'success' && bookingId) {
      // Payment was successful, verify payment status first, then fetch full booking details
      const fetchBookingDetails = async () => {
        try {
          console.log('Verifying payment status for booking:', bookingId);
          
          // First, verify the booking exists and payment status is PAID
          let bookingResponse;
          if (isFleetUser()) {
            console.log("🚛 Using fleet v1 booking details API");
            bookingResponse = await authService.fleetGetBookingDetails(bookingId);
          } else {
            console.log("👤 Using general booking details API");
            bookingResponse = await bookingService.getBookingById(bookingId);
          }
          
          if (!bookingResponse.success || !bookingResponse.data) {
            setError('Booking not found. Payment may not have been processed correctly.');
            return;
          }
          
          const booking = bookingResponse.data;
          
          // Check if payment status is actually PAID
          const actualPaymentStatus = booking.payment_status || booking.paymentStatus;
          if (actualPaymentStatus !== 'PAID') {
            setError(`Payment verification failed. Payment status: ${actualPaymentStatus}. Please contact support.`);
            return;
          }
          
          console.log('Payment verified as PAID. Fetching full booking details...');
          
          // Extract driver information using the same helper logic
          const assignedDriver = booking.driver_matching?.assigned_driver || booking.driver || booking.vehicle?.assigned_driver;
          const vehicle = booking.vehicle;
          const operator = assignedDriver?.operator || vehicle?.operator || booking.operator;
          const driverMatching = booking.driver_matching || {};
          const driverStatus = driverMatching?.success 
            ? driverMatching?.message || 'Driver assigned'
            : 'Waiting for driver assignment...';
          const matchingStatus = driverMatching?.success ? 'assigned' : 'searching';
          const driverName = assignedDriver?.driver_name || assignedDriver?.name || 'N/A';
          const driverPhone = assignedDriver?.phone || assignedDriver?.contact_phone || assignedDriver?.phone_number || 'N/A';
          const vendorName = operator?.name || operator?.operator_name || vehicle?.operator_name || 'N/A';
          const vendorContact = operator?.contact_info || operator?.phone || operator?.contact_phone || 'N/A';
          const vehicleNumber = vehicle?.vehicle_number || vehicle?.registration_number || vehicle?.ambulance_number || 'N/A';

          // Extract fare information
          const estimatedFare = booking.estimated_fare || booking.estimatedFare || booking.final_fare || booking.finalFare;
          const finalFare = booking.final_fare || booking.finalFare || estimatedFare;

          // Extract addresses - use direct fields first (like cash payment format)
          const pickupAddress = booking.pickup_address || booking.pickupAddress || booking.pickup_location?.address || booking.pickupLocation?.address || 'N/A';
          const destinationAddress = booking.dropoff_address || booking.dropoffAddress || booking.destination_address || booking.destinationAddress || booking.dropoff_location?.address || booking.dropoffLocation?.address || 'N/A';

          // Show success modal with same format as cash payment, just add payment confirmation
          setSuccessModal({
            isOpen: true,
            data: {
              bookingId: bookingId,
              patientName: booking.patient_name || booking.patientName || 'N/A',
              hospitalName: "Selected Hospital", // Same as cash payment
              ambulanceType: booking.vehicle?.type?.name || booking.vehicle?.name || booking.ambulanceType || "Ambulance",
              estimatedFare: finalFare, // Use finalFare like cash payment
              finalFare: finalFare,
              driverStatus: driverStatus,
              driverName: driverName,
              driverPhone: driverPhone,
              vendorName: vendorName,
              vendorContact: vendorContact,
              vehicleNumber: vehicleNumber,
              pickupAddress: pickupAddress, // Direct string like cash payment
              destinationAddress: destinationAddress, // Direct string like cash payment
              bookingType: booking.booking_type || booking.bookingType,
              direction: booking.journey_direction || booking.direction || booking.booking_direction,
              matchingStatus: matchingStatus,
              // Add payment confirmation
              paymentStatus: 'success',
              paymentId: paymentId
            }
          });

          console.log('Booking details loaded:', booking);
        } catch (error) {
          console.error('Error fetching booking details after payment:', error);
          // Fallback if API call fails
          setSuccessModal({
            isOpen: true,
            data: {
              bookingId: bookingId,
              paymentStatus: 'success',
              paymentId: paymentId,
              message: 'Payment completed successfully! Your booking is confirmed. (Unable to load full booking details)'
            }
          });
        }
      };

      fetchBookingDetails();

      // Clear URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Helper: Validate mobile number is complete
  const validateMobileNumber = (phone, isFleetUser = false, isHospitalUser = false) => {
    if (!phone || !phone.trim()) {
      return { isValid: false, message: "Mobile number is required. Please enter a complete 10-digit mobile number." };
    }

    const phoneDigits = phone.replace(/\D/g, ""); // Remove all non-digits

    // For fleet dashboard or hospital dashboard, allow 1-10 digits (flexible international format)
    // But if a number is started, it should be at least 10 digits to be considered complete
    if (isFleetUser || isHospitalUser) {
      if (phoneDigits.length > 0 && phoneDigits.length < 10) {
        return { isValid: false, message: "Mobile number is incomplete. Please enter a complete 10-digit mobile number." };
      }
      if (phoneDigits.length > 10) {
        return { isValid: false, message: "Mobile number is too long. Please enter a valid 10-digit mobile number." };
      }
      // For hospital/fleet, at least 10 digits is required if phone is provided
      if (phoneDigits.length < 10) {
        return { isValid: false, message: "Mobile number is incomplete. Please enter a complete 10-digit mobile number." };
      }
    } else {
      // For regular users, validate Indian phone number format (exactly 10 digits starting with 6-9)
      if (phoneDigits.length !== 10) {
        return { isValid: false, message: "Mobile number must be exactly 10 digits. Please enter a complete mobile number." };
      }
      if (!/^[6-9]\d{9}$/.test(phoneDigits)) {
        return { isValid: false, message: "Invalid mobile number. Please enter a valid 10-digit mobile number starting with 6, 7, 8, or 9." };
      }
    }

    return { isValid: true, message: "" };
  };

  // Helper: Validate booking prerequisites
  const validateBookingPrerequisites = () => {
    if (!selectedAmbulance) {
      setValidationErrorModal({
        isOpen: true,
        title: "Validation Error",
        message: "Please select an ambulance before proceeding with the booking.",
      });
      throw new Error("Please select an ambulance first");
    }

    if (!bookingFormData) {
      setValidationErrorModal({
        isOpen: true,
        title: "Validation Error",
        message: "Booking form data is missing. Please fill in all required information.",
      });
      throw new Error("Booking form data is missing");
    }

    // Validate patient name
    if (!bookingFormData.name || !bookingFormData.name.trim()) {
      setValidationErrorModal({
        isOpen: true,
        title: "Validation Error",
        message: "Patient name is required. Please enter the patient's name.",
      });
      throw new Error("Patient name is required");
    }

    // Validate locations
    if (!pickupCoordinates || !dropCoordinates) {
      setValidationErrorModal({
        isOpen: true,
        title: "Validation Error",
        message: "Both pickup and drop locations are required. Please select both locations on the map.",
      });
      throw new Error("Pickup and drop locations are required");
    }

    // Validate mobile number - if phone is provided, it must be complete
    // If no phone, email must be provided
    const hasPhone = bookingFormData.phone && bookingFormData.phone.trim();
    const hasEmail = bookingFormData.email && bookingFormData.email.trim();

    if (!hasPhone && !hasEmail) {
      setValidationErrorModal({
        isOpen: true,
        title: "Validation Error",
        message: "Either mobile number or email is required. Please provide at least one contact method.",
      });
      throw new Error("Contact information (mobile number or email) is required");
    }

    // If phone is provided, validate it's complete
    if (hasPhone) {
      const phoneValidation = validateMobileNumber(
        bookingFormData.phone,
        isFleetUser(),
        isHospitalUser()
      );
      if (!phoneValidation.isValid) {
        setValidationErrorModal({
          isOpen: true,
          title: "Incomplete Mobile Number",
          message: phoneValidation.message,
        });
        throw new Error(phoneValidation.message);
      }
    }

    // Validate scheduled booking date and time
    if (bookingFormData.bookingType === "scheduled") {
      if (!bookingFormData.date || !bookingFormData.date.trim()) {
        setValidationErrorModal({
          isOpen: true,
          title: "Validation Error",
          message: "Date is required for scheduled bookings. Please select a date.",
        });
        throw new Error("Date is required for scheduled bookings");
      }
      if (!bookingFormData.time || !bookingFormData.time.trim()) {
        setValidationErrorModal({
          isOpen: true,
          title: "Validation Error",
          message: "Time is required for scheduled bookings. Please select a time.",
        });
        throw new Error("Time is required for scheduled bookings");
      }
    }
  };

  // Helper: Check if user is a hospital user
  const isHospitalUser = () => {
    return user?.role === 'HOSPITAL_USER' ||
           user?.role === 'hospital' ||
           user?.role === 'Hospital';
  };

  // Helper: Check if user is a fleet user
  const isFleetUser = () => {
    return user?.role === 'TENANT_ADMIN' ||
           user?.role === 'TENANT_USER' ||
           user?.role === 'fleet_operator' ||
           user?.role === 'Fleet';
  };

  // Helper: Get hospital ID from user profile and localStorage (same logic as createBooking)
  const getHospitalIdFromUser = () => {
    // PRIORITY 1: Check localStorage first (most reliable source)
    if (typeof window !== 'undefined') {
      try {
        // Priority 1: Get from dedicated key
        const hospitalIdKey = API_CONFIG.STORAGE_KEYS.HOSPITAL_ID;
        const hospitalIdFromKey = localStorage.getItem(hospitalIdKey);
        
        if (hospitalIdFromKey) {
          return String(hospitalIdFromKey);
        }
        
        // Priority 2: Get from hospital_data
        const hospitalDataKey = API_CONFIG.STORAGE_KEYS.HOSPITAL_DATA;
        const hospitalDataStr = localStorage.getItem(hospitalDataKey);
        if (hospitalDataStr) {
          try {
            const hospitalData = JSON.parse(hospitalDataStr);
            const hospitalIdFromData = hospitalData.hospital_id || hospitalData.id;
            if (hospitalIdFromData) {
              // Also store it in the dedicated key for future use
              localStorage.setItem(hospitalIdKey, String(hospitalIdFromData));
              return String(hospitalIdFromData);
            }
          } catch (e) {
            // Failed to parse hospital_data
          }
        }
        
        // Priority 3: Get from user_data
        const userDataKey = API_CONFIG.STORAGE_KEYS.USER_DATA;
        const userDataStr = localStorage.getItem(userDataKey);
        if (userDataStr) {
          try {
            const parsedUser = JSON.parse(userDataStr);
            const hospitalIdFromStorage =
              parsedUser.hospital_id ||
              parsedUser.hospitalId ||
              parsedUser.hospital?.hospital_id ||
              parsedUser.hospital?.id ||
              parsedUser.operator_id; // Sometimes operator_id might be the hospital_id
            
            if (hospitalIdFromStorage) {
              // Also store it in the dedicated key for future use
              localStorage.setItem(hospitalIdKey, String(hospitalIdFromStorage));
              return String(hospitalIdFromStorage);
            }
          } catch (e) {
            // Failed to parse user data
          }
        }
        
        // Priority 4: Try alternative keys
        const alternativeKeys = ['user', 'userData', 'auth_user', 'currentUser'];
        for (const key of alternativeKeys) {
          const altData = localStorage.getItem(key);
          if (altData) {
            try {
              const parsed = JSON.parse(altData);
              const altHospitalId = parsed.hospital_id || parsed.hospitalId || parsed.hospital?.hospital_id;
              if (altHospitalId) {
                // Also store it in the dedicated key for future use
                localStorage.setItem(hospitalIdKey, String(altHospitalId));
                return String(altHospitalId);
              }
            } catch (e) {
              // Failed to parse data from key
            }
          }
        }
      } catch (error) {
        console.error('Error getting hospital ID from localStorage:', error);
      }
    }
    
    // PRIORITY 2: Fallback to user object from auth context
    return user?.hospital_id || 
           user?.hospitalId || 
           user?.hospital?.hospital_id ||
           user?.hospitals?.[0]?.hospital_id ||
           null;
  };

  // Handle assign button click - manual allocation flow only
  // This function ONLY handles manual allocation: creates booking, starts polling, opens modal
  const handleAssignCandidate = async (candidate) => {
    if (!candidate || !bookingFormData || !pickupCoordinates || !dropCoordinates) {
      console.error('❌ [Manual Assign] Missing required data for assignment');
      setError('Please ensure all booking details are filled before assigning.');
      return;
    }

    console.log('🚑 [Manual Assign] Starting manual allocation flow for candidate:', candidate);

    // Set selectedAmbulance to the candidate
    handleAmbulanceSelect(candidate);

    // Ensure allocation type is set to Manual and preserve paymentType
    const updatedBookingFormData = {
      ...bookingFormData,
      allocationType: "Manual"
    };
    setBookingFormData(updatedBookingFormData);

    setIsPollingAllocation(true);

    try {
      // Validate prerequisites
      if (!candidate) {
        throw new Error("Please select an ambulance first");
      }
      if (!bookingFormData) {
        throw new Error("Booking form data is missing");
      }

      // Check if this is a fleet user or hospital user
      const isFleet = isFleetUser();
      const isHospital = isHospitalUser();

      let hospitalId;
      if (isFleet) {
        // For fleet users, get hospitalId from bookingFormData (selected hospital partner)
        hospitalId = bookingFormData?.hospitalPartnerId;
        if (!hospitalId) {
          throw new Error('Hospital ID not found. Please select a hospital partner before assigning.');
        }
        console.log('🚛 [Manual Assign] Fleet user - using hospital partner ID:', hospitalId);
      } else if (isHospital) {
        // For hospital users, get hospitalId from user profile
        hospitalId = getHospitalIdFromUser();
        if (!hospitalId) {
          throw new Error('Hospital ID not found. Please ensure you are logged in as a hospital user.');
        }
        console.log('🏥 [Manual Assign] Hospital user - using hospital ID:', hospitalId);
      } else {
        throw new Error('Invalid user type. Please ensure you are logged in as a fleet or hospital user.');
      }

      // Determine payment method - check multiple sources in priority order:
      // 1. Current paymentType from ChooseAmbulance card (most up-to-date)
      // 2. updatedBookingFormData.paymentType
      // 3. bookingFormData.paymentType
      const paymentTypeValue = currentPaymentTypeRef.current || 
                               updatedBookingFormData.paymentType || 
                               bookingFormData.paymentType;
      console.log('💳 [Manual Allocation] Payment type sources:', {
        fromRef: currentPaymentTypeRef.current,
        fromUpdatedFormData: updatedBookingFormData.paymentType,
        fromBookingFormData: bookingFormData.paymentType,
        finalValue: paymentTypeValue
      });
      console.log('💳 [Manual Allocation] Full bookingFormData:', bookingFormData);
      console.log('💳 [Manual Allocation] Full updatedBookingFormData:', updatedBookingFormData);
      
      // Determine payment method with case-insensitive check
      let paymentMethod = "PAY_LATER"; // Default
      if (paymentTypeValue) {
        const paymentTypeLower = String(paymentTypeValue).toLowerCase();
        if (paymentTypeLower === "online") {
          paymentMethod = "ONLINE";
        } else if (paymentTypeLower === "cash") {
          paymentMethod = "CASH";
        }
      }
      
      console.log('💳 [Manual Allocation] Determined payment method:', paymentMethod);
      console.log('💳 [Manual Allocation] Payment method will be sent in payload:', paymentMethod);

      // Build filters object
      const filters = {};
      
      // Add operator_id filter if partner is selected (not "all" or "all_partners")
      // When "all_partners" is selected, don't filter by operator - send to all
      if (selectedPartner !== "all" && selectedPartner !== "all_partners") {
        let operatorId = null;
        if (typeof selectedPartner === 'string' && selectedPartner.includes('_')) {
          operatorId = selectedPartner.split('_')[0];
        } else {
          operatorId = selectedPartner;
        }
        if (operatorId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(operatorId)) {
          filters.operator_id = operatorId;
        }
      }
      
      // Add distance range filter
      if (kmRange) {
        filters.distance_range_km = kmRange;
      }
      
      // Add ambulance type filter
      if (selectedAmbulanceTypes.length > 0) {
        filters.ambulance_type_ids = selectedAmbulanceTypes;
      } else if (candidate.type_id) {
        filters.ambulance_type_ids = [candidate.type_id];
      }

      // Build selected object with driver and vehicle
      const selected = {};
      if (candidate.driver?.id) {
        selected.driver_id = candidate.driver.id;
      }
      if (candidate.vehicle_id) {
        selected.vehicle_id = candidate.vehicle_id;
      }

      // Format phone number to exactly 10 digits (remove all non-digits, then take last 10)
      let formattedPhone = "";
      if (updatedBookingFormData.phone) {
        // Remove all non-digit characters
        const digitsOnly = updatedBookingFormData.phone.replace(/\D/g, "");
        // Take last 10 digits (in case there's a country code like +91)
        if (digitsOnly.length >= 10) {
          formattedPhone = digitsOnly.slice(-10);
        } else if (digitsOnly.length > 0) {
          // Pad with leading zero if less than 10 digits
          formattedPhone = digitsOnly.padStart(10, '0');
        }
      }
      
      if (!formattedPhone || formattedPhone.length !== 10) {
        throw new Error('Phone number must be exactly 10 digits. Please check the phone number in the booking form.');
      }

      // Build booking_payload for manual allocation API
      const bookingPayload = {
        hospitalId: hospitalId,
        patientName: updatedBookingFormData.name || "",
        patientPhone: formattedPhone,
        bookingDirection: updatedBookingFormData.direction?.toUpperCase() || "OUTBOUND",
        pickupLocation: {
          address: updatedBookingFormData.pickupLocation || "",
          latitude: pickupCoordinates.latitude,
          longitude: pickupCoordinates.longitude
        },
        dropoffLocation: {
          address: updatedBookingFormData.dropLocation || "",
          latitude: dropCoordinates.latitude,
          longitude: dropCoordinates.longitude
        },
        paymentMethod: paymentMethod,
        filters: filters,
        selected: selected
      };

      // Add online payment details if payment method is Online
      if (paymentMethod === "ONLINE") {
        const estimatedFare = candidate?.estimated_fare || 
                             (candidate?.type_id && fareEstimates[candidate.type_id]) ||
                             null;
        
        bookingPayload.paymentDetails = {
          paymentGateway: "razorpay", // Default payment gateway
          currency: "INR", // Default currency
          amount: estimatedFare || null, // Estimated fare amount
          paymentMethod: "ONLINE",
          // Include patient contact info for payment gateway prefill
          patientPhone: formattedPhone,
          patientEmail: updatedBookingFormData.email || null,
          patientName: updatedBookingFormData.name || ""
        };
        
        console.log('💳 [Manual Allocation] Added online payment details to booking payload:', bookingPayload.paymentDetails);
      }

      // If there's an existing bookingId (from previous manual allocation), add it to payload
      // This allows backend to assign driver to the same booking instead of creating a new one
      if (currentBookingId) {
        bookingPayload.bookingId = String(currentBookingId);
        console.log('📋 [Manual Assign] Adding existing bookingId to payload:', bookingPayload.bookingId);
        console.log('📋 [Manual Assign] This will assign driver to existing booking instead of creating new one');
      } else {
        console.log('📋 [Manual Assign] No existing bookingId - will create new booking');
      }

      console.log('📋 [Manual Assign] Calling manual allocation API with payload:', bookingPayload);

      // Call manual allocation API - use fleet API for fleet users, hospital API for hospital users
      let result;
      if (isFleet) {
        console.log('🚛 [Manual Assign] Using fleet manual allocation API');
        result = await authService.fleetStartManualAllocation(bookingPayload);
      } else {
        console.log('🏥 [Manual Assign] Using hospital manual allocation API');
        result = await hospitalService.startManualAllocation(bookingPayload);
      }

      if (!result.success || !result.data) {
        throw new Error(result.message || "Manual allocation failed");
      }

      const bookingId = result.data.booking_id;
      // Fleet API response structure: response_id, status, expires_in_sec are at top level in data
      // Hospital API has response_id at top level, status and expires_in_sec may be nested
      const responseId = isFleet 
        ? result.data.response_id
        : result.data.response_id;
      
      const status = isFleet 
        ? result.data.status
        : (result.data.driver_response?.status || result.data.status);
      
      const expiresInSec = isFleet
        ? result.data.expires_in_sec
        : (result.data.driver_response?.expires_in_sec || result.data.expires_in_sec);

      if (!bookingId) {
        throw new Error("No booking ID received from manual allocation API");
      }

      console.log('✅ [Manual Assign] Manual allocation started successfully:', {
        booking_id: bookingId,
        response_id: responseId,
        status: status,
        expires_in_sec: expiresInSec,
        ui_state: result.data.ui_state,
        wasReassignment: !!currentBookingId,
        previousBookingId: currentBookingId,
        isFleetUser: isFleet,
        fullResponse: result.data
      });

      // Store booking ID and response data for polling
      // If we sent an existing bookingId, the backend should return the same bookingId
      // If it's a new booking, we'll get a new bookingId
      setCurrentBookingId(bookingId);
      
      if (currentBookingId && currentBookingId !== bookingId) {
        console.warn('⚠️ [Manual Assign] BookingId changed during reassignment:', {
          previous: currentBookingId,
          new: bookingId
        });
      } else if (currentBookingId && currentBookingId === bookingId) {
        console.log('✅ [Manual Assign] Driver assigned to existing booking:', bookingId);
      }
      
      // Get estimated fare from candidate or fareEstimates
      const estimatedFare = candidate?.estimated_fare || 
                           (candidate?.type_id && fareEstimates[candidate.type_id]) ||
                           null;

      // Get hospital information
      const hospitalName = user?.hospital?.name || 
                          user?.hospital_name || 
                          bookingFormData?.hospitalName ||
                          'Hospital';

      // Set initial allocation status from response
      // Map response to match the expected allocation status structure
      // Fleet API response: status, expires_in_sec, response_id are at top level in data
      // Hospital API: response_id at top level, status and expires_in_sec may be nested
      const initialAllocationStatus = isFleet ? {
        booking_id: bookingId,
        response_id: responseId,
        allocation_type: result.data.allocation_type || 'MANUAL',
        ui_state: result.data.ui_state || status || 'WAITING',
        driver_response: {
          response_id: responseId,
          status: status || 'PENDING',
          expires_in_sec: expiresInSec || 30,
          responded_at: result.data.driver_response?.responded_at || null
        },
        driver: result.data.driver,
        vehicle: result.data.vehicle,
        estimated_fare: estimatedFare,
        hospital_name: hospitalName,
        current_driver_id: result.data.driver?.id || null
      } : {
        booking_id: bookingId,
        response_id: responseId,
        allocation_type: 'MANUAL',
        ui_state: status,
        driver_response: {
          response_id: responseId,
          status: status,
          expires_in_sec: expiresInSec || 30,
          responded_at: result.data.driver_response?.responded_at || null
        },
        driver: result.data.driver,
        vehicle: result.data.vehicle,
        estimated_fare: estimatedFare,
        hospital_name: hospitalName
      };

      setAllocationStatus(initialAllocationStatus);

      // Stop any existing polling before starting new polling (for reassignment scenario)
      if (allocationPollingIntervalRef.current) {
        console.log('🛑 [Manual Assign] Stopping existing polling before starting new allocation');
        clearInterval(allocationPollingIntervalRef.current);
        allocationPollingIntervalRef.current = null;
      }

      // Start polling allocation status - this is the ONLY polling API for manual allocation
      startPollingAllocationStatus(bookingId);

      // Open manual allocation modal - this shows the polling status
      if (openManualAllocationModalRef.current) {
        console.log('✅ [Manual Assign] Opening manual allocation modal');
        openManualAllocationModalRef.current();
      } else {
        console.warn('⚠️ [Manual Assign] Manual allocation modal ref not available');
      }

    } catch (error) {
      console.error('❌ [Manual Assign] Error in manual allocation flow:', error);
      setError(error.response?.data?.message || error.message || 'Failed to assign candidate');
      setIsPollingAllocation(false);
      setAssigningCandidateId(null);
      setCurrentBookingId(null);
    }
  };

  // Start polling allocation status - ONLY for manual allocation flow
  // This polls the manual allocation API to get driver response status
  const startPollingAllocationStatus = (bookingId) => {
    // Clear any existing interval
    if (allocationPollingIntervalRef.current) {
      clearInterval(allocationPollingIntervalRef.current);
    }

    // Poll immediately first time
    pollAllocationStatus(bookingId);

    // Then poll every 3 seconds
    const interval = setInterval(() => {
      pollAllocationStatus(bookingId);
    }, 3000);

    allocationPollingIntervalRef.current = interval;

    // Stop polling after 2 minutes (40 polls)
    setTimeout(() => {
      if (allocationPollingIntervalRef.current === interval) {
        clearInterval(interval);
        allocationPollingIntervalRef.current = null;
        setIsPollingAllocation(false);
        setAssigningCandidateId(null);
      }
    }, 120000);
  };

  // Poll allocation status - ONLY for manual allocation flow
  // This is the ONLY polling API call for manual allocation
  const pollAllocationStatus = async (bookingId) => {
    try {
      // Use fleet API for fleet users, hospital API for hospital users
      let response;
      if (isFleetUser()) {
        response = await authService.fleetGetAllocationStatus(bookingId);
      } else {
        response = await hospitalService.getAllocationStatus(bookingId);
      }

      if (response.success && response.data) {
        const statusData = response.data;
        
        // Normalize response structure for fleet API (which has response_id nested in driver_response)
        // Fleet API: driver_response.response_id, Hospital API: response_id at top level
        const normalizedStatusData = isFleetUser() ? {
          ...statusData,
          // Extract response_id from driver_response for fleet API
          response_id: statusData.driver_response?.response_id || statusData.response_id,
          // Ensure driver_response structure is consistent
          driver_response: {
            ...statusData.driver_response,
            response_id: statusData.driver_response?.response_id || statusData.response_id,
            status: statusData.driver_response?.status || statusData.ui_state,
            expires_in_sec: statusData.driver_response?.expires_in_sec,
            responded_at: statusData.driver_response?.responded_at
          }
        } : statusData;
        
        // Merge with existing allocation status to preserve driver/vehicle data from initial response
        // This works for both manual and automatic allocation flows
        setAllocationStatus(prevStatus => ({
          ...prevStatus,
          ...normalizedStatusData,
          // Use latest driver and vehicle data from API (real-time updates)
          driver: normalizedStatusData.driver || prevStatus?.driver,
          vehicle: normalizedStatusData.vehicle || prevStatus?.vehicle,
          // Use latest estimated_fare from API (real-time updates)
          estimated_fare: normalizedStatusData.estimated_fare || prevStatus?.estimated_fare,
          // Preserve allocation_type
          allocation_type: normalizedStatusData.allocation_type || prevStatus?.allocation_type || 'AUTOMATIC',
          // Store operator name if available
          operator_name: normalizedStatusData.operator_name || prevStatus?.operator_name || normalizedStatusData.operator?.name || prevStatus?.operator?.name,
          // Ensure response_id is available at top level for modal
          response_id: normalizedStatusData.response_id || normalizedStatusData.driver_response?.response_id || prevStatus?.response_id
        }));

        // Check if we should stop polling based on UI state or driver response
        // For fleet users in automatic flow: Stop only when driver accepts, not on timeout
        const shouldStopPolling = isFleetUser() && allocationType === "Automatic" 
          ? (normalizedStatusData.driver_response?.status === 'ACCEPTED' || 
             normalizedStatusData.ui_state === 'ACCEPTED' ||
             normalizedStatusData.ui_state === 'COMPLETED')
          : ([
              'TIMEOUT_PICK_ANOTHER',
              'ACCEPTED',
              'ACCEPTED_GO_CONFIRM',
              'DECLINED',
              'COMPLETED'
            ].includes(normalizedStatusData.ui_state) ||
             normalizedStatusData.driver_response?.status === 'DECLINED' ||
             normalizedStatusData.driver_response?.status === 'ACCEPTED');

        if (shouldStopPolling && allocationPollingIntervalRef.current) {
          clearInterval(allocationPollingIntervalRef.current);
          allocationPollingIntervalRef.current = null;
          setIsPollingAllocation(false);
          setAssigningCandidateId(null);
          
          // For fleet users: If driver accepted, stop automatic allocation iteration
          if (isFleetUser() && allocationType === "Automatic" && 
              (normalizedStatusData.driver_response?.status === 'ACCEPTED' || normalizedStatusData.ui_state === 'ACCEPTED')) {
            console.log('✅ [FLEET AUTO-ITERATION] Driver accepted - stopping auto-iteration');
            setAutomaticAllocationActive(false);
            // Clear timer
            if (automaticTimerRef.current) {
              clearInterval(automaticTimerRef.current);
              automaticTimerRef.current = null;
            }
            automaticTimerStartTimeRef.current = null;
          }
          
        }

        // Handle different UI states
        if (normalizedStatusData.ui_state === 'TIMEOUT_PICK_ANOTHER') {
          setError('Driver did not respond in time. Please select another ambulance.');
        } else if (normalizedStatusData.driver_response?.status === 'ACCEPTED') {
          // Driver accepted - show success
          // Try to get driver information from multiple sources
          let driverName = 'N/A';
          let driverPhone = 'N/A';
          let vehicleNumber = 'N/A';
          
          // First, try to get from candidates array (if available)
          let assignedCandidate = null;
          if (Array.isArray(candidates) && candidates.length > 0 && selectedAmbulance) {
            assignedCandidate = candidates.find(c => 
              (c && selectedAmbulance && c.vehicle_id === selectedAmbulance.vehicle_id) || 
              (c && selectedAmbulance && c.type_id === selectedAmbulance.type_id)
            ) || null;
          }
          
          if (assignedCandidate && assignedCandidate.driver) {
            driverName = assignedCandidate.driver.name || 
                        assignedCandidate.driver.driver_name || 
                        driverName;
            driverPhone = assignedCandidate.driver.phone || 
                         assignedCandidate.driver.phone_number || 
                         assignedCandidate.driver.contact_phone || 
                         driverPhone;
            vehicleNumber = assignedCandidate.plate_number || 
                           assignedCandidate.vehicle_number || 
                           vehicleNumber;
          } else if (selectedAmbulance && selectedAmbulance.driver) {
            // Fallback to selectedAmbulance
            driverName = selectedAmbulance.driver.name || 
                        selectedAmbulance.driver.driver_name || 
                        driverName;
            driverPhone = selectedAmbulance.driver.phone || 
                         selectedAmbulance.driver.phone_number || 
                         selectedAmbulance.driver.contact_phone || 
                         driverPhone;
            vehicleNumber = selectedAmbulance.plate_number || 
                           selectedAmbulance.vehicle_number || 
                           vehicleNumber;
          }
          
          // Try to fetch booking details to get actual driver information
          try {
            const bookingResponse = await hospitalService.getBookingById(bookingId);
            if (bookingResponse && bookingResponse.success && bookingResponse.data) {
              const booking = bookingResponse.data;
              const driverInfo = extractDriverInfo({ data: booking });
              
              // Use booking data if available and valid
              if (driverInfo && driverInfo.driverName && 
                  driverInfo.driverName !== 'N/A' && 
                  driverInfo.driverName.trim() !== '') {
                driverName = driverInfo.driverName;
              }
              if (driverInfo && driverInfo.driverPhone && 
                  driverInfo.driverPhone !== 'N/A' && 
                  driverInfo.driverPhone.trim() !== '') {
                driverPhone = driverInfo.driverPhone;
              }
              if (driverInfo && driverInfo.vehicleNumber && 
                  driverInfo.vehicleNumber !== 'N/A' && 
                  driverInfo.vehicleNumber.trim() !== '') {
                vehicleNumber = driverInfo.vehicleNumber;
              }
            }
          } catch (bookingError) {
            console.warn('⚠️ [Allocation Poll] Could not fetch booking details for driver info:', bookingError);
            // Continue with candidate data if booking fetch fails - this is not critical
          }
          
          const finalFare = calculateEstimatedFare(selectedAmbulance, fareEstimates) || 
                           assignedCandidate?.estimated_fare || 
                           0;
          
          const successData = {
            bookingId: bookingId,
            patientName: bookingFormData.name,
            phoneNumber: bookingFormData.phone,
            pickupAddress: bookingFormData.pickupLocation,
            destinationAddress: bookingFormData.dropLocation,
            ambulanceType: selectedAmbulance?.name || 
                          assignedCandidate?.name || 
                          "Ambulance",
            estimatedFare: finalFare,
            paymentType: bookingFormData.paymentType,
            driverName: driverName,
            driverPhone: driverPhone,
            vehicleNumber: vehicleNumber,
            allocationType: 'MANUAL'
          };
          
          setSuccessModal({ isOpen: true, data: successData });
        } else if (statusData.driver_response?.status === 'DECLINED') {
          // Stop polling and reset assigning state
          if (allocationPollingIntervalRef.current) {
            clearInterval(allocationPollingIntervalRef.current);
            allocationPollingIntervalRef.current = null;
          }
          setIsPollingAllocation(false);
          setAssigningCandidateId(null);
          // Show notification that auto-dismisses after 5 seconds
          showError(
            'No Response',
            'Driver did not respond to the request. Please select another ambulance.'
          );
          // Clear any existing error state
          setError('');
        }
      }
    } catch (error) {
      console.error('❌ [Allocation Poll] Error polling allocation status:', error);
      // Don't stop polling on error, just log it
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (allocationPollingIntervalRef.current) {
        clearInterval(allocationPollingIntervalRef.current);
        allocationPollingIntervalRef.current = null;
      }
    };
  }, []);

  // Handle refetch candidates with bookingId (for fleet users after stop allocation)
  const handleRefetchCandidatesWithBookingId = async (bookingIdToRefetch) => {
    if (!pickupCoordinates || !dropCoordinates || !bookingFormData) {
      console.warn('Missing required data for candidates refetch');
      return;
    }

    // Only refetch for fleet users
    if (!isFleetUser()) {
      console.log('⚠️ [Refetch Candidates] Not a fleet user - skipping refetch');
      return;
    }

    setCandidatesLoading(true);
    setCandidatesError(null);
    setHasSearchedCandidates(true);

    try {
      // Determine payment method
      const paymentMethod = bookingFormData.paymentType === "Online" ? "ONLINE" : 
                           bookingFormData.paymentType === "Cash" ? "CASH" : "PAY_LATER";

      console.log('🚛 [Refetch Candidates] Fleet user - refetching candidates with bookingId:', bookingIdToRefetch);
      
      // For fleet users, get hospitalId from bookingFormData (selected hospital partner)
      const hospitalId = bookingFormData?.hospitalPartnerId;
      
      if (!hospitalId) {
        console.error('❌ [Refetch Candidates] Hospital ID not found for fleet user');
        throw new Error('Hospital ID not found. Please select a hospital partner.');
      }

      // Prepare fleet candidates request data with bookingId
      const fleetCandidatesData = {
        bookingId: String(bookingIdToRefetch), // BookingId from stop-allocation - excludes cancelled driver
        hospitalId: hospitalId,
        pickupLocation: {
          address: bookingFormData.pickupLocation || "",
          latitude: pickupCoordinates.latitude,
          longitude: pickupCoordinates.longitude
        },
        dropoffLocation: {
          address: bookingFormData.dropLocation || "",
          latitude: dropCoordinates.latitude,
          longitude: dropCoordinates.longitude
        },
        distance_range_km: kmRange || 6,
        payment_method: paymentMethod
      };

      // Add ambulance type IDs if available
      if (selectedAmbulanceTypes.length > 0) {
        fleetCandidatesData.ambulance_type_ids = selectedAmbulanceTypes;
      } else if (selectedAmbulance?.type_id) {
        fleetCandidatesData.ambulance_type_ids = [selectedAmbulance.type_id];
      } else if (availableAmbulanceTypes.length > 0) {
        const typeIds = availableAmbulanceTypes.map(type => type.type_id).filter(Boolean);
        if (typeIds.length > 0) {
          fleetCandidatesData.ambulance_type_ids = typeIds;
        }
      }

      console.log('🔍 [Refetch Candidates] Fetching fleet candidates with payload:', JSON.stringify(fleetCandidatesData, null, 2));

      const response = await authService.fleetGetCandidates(fleetCandidatesData);
      
      console.log('📥 [Refetch Candidates] Fleet API Response:', {
        success: response.success,
        hasData: !!response.data,
        hasCandidates: !!response.data?.candidates,
        candidatesCount: response.data?.candidates?.length || 0
      });
      
      if (response.success && response.data?.candidates) {
        // Transform candidates to match ambulance UI format
        const transformedCandidates = response.data.candidates.map(candidate => ({
          vehicle_id: candidate.vehicle_id,
          type_id: candidate.type?.id,
          name: candidate.type?.name,
          plate_number: candidate.plate_number,
          driver: {
            id: candidate.driver?.id,
            name: candidate.driver?.name,
            phone: candidate.driver?.phone,
            rating: candidate.driver?.rating,
            total_rides: candidate.driver?.total_rides
          },
          operator: {
            id: candidate.operator?.id,
            name: candidate.operator?.name,
            is_partner: candidate.operator?.is_partner
          },
          eta_minutes: candidate.eta_minutes,
          distanceFromPickup: candidate.distance_from_pickup_km,
          estimated_fare: candidate.estimated_fare,
          is_partner: candidate.operator?.is_partner || false,
          operator_name: candidate.operator?.name,
          pricing_source: candidate.pricing_source ?? null,
          pricing_model: candidate.pricing_model ?? null,
          pricing_breakdown: candidate.pricing_breakdown ?? candidate.breakdown ?? null
        }));

        console.log('✅ [Refetch Candidates] Fleet candidates received:', transformedCandidates.length);
        
        // Filter candidates based on distance if kmRange is set
        let filteredCandidates = transformedCandidates;
        if (kmRange && kmRange < 50) {
          filteredCandidates = filteredCandidates.filter(candidate => 
            candidate.distanceFromPickup <= kmRange
          );
        }

        // Filter by ambulance type if selected
        if (selectedAmbulanceTypes.length > 0) {
          filteredCandidates = filteredCandidates.filter(candidate =>
            selectedAmbulanceTypes.includes(candidate.type_id)
          );
        }

        // Update candidates state with real-time data
        setCandidates(filteredCandidates);
        setCandidatesError(null);
        console.log('✅ [Refetch Candidates] Candidates updated in UI:', filteredCandidates.length);
      } else {
        if (response.success && response.data) {
          console.warn('⚠️ [Refetch Candidates] Fleet API returned success but no candidates array');
          setCandidatesError('No ambulances available matching your criteria. Please try adjusting your filters.');
        } else {
          console.error('❌ [Refetch Candidates] Fleet API returned unsuccessful response');
          setCandidatesError(response.message || 'No candidates found. Please try adjusting your filters.');
        }
        setCandidates([]);
      }
    } catch (error) {
      console.error('❌ [Refetch Candidates] Error refetching candidates:', error);
      setCandidatesError(
        error.response?.data?.message || 
        error.message || 
        'Failed to refetch candidates. Please try again.'
      );
      setCandidates([]);
    } finally {
      setCandidatesLoading(false);
    }
  };

  // Handle manual search - fetch candidates from API
  const handleManualSearch = async () => {
    if (!pickupCoordinates || !dropCoordinates || !bookingFormData) {
      console.warn('Missing required data for manual search');
      return;
    }

    setCandidatesLoading(true);
    setCandidatesError(null);
    setHasSearchedCandidates(true); // Mark that search has been performed

    try {
      // Determine payment method
      const paymentMethod = bookingFormData.paymentType === "Online" ? "ONLINE" : 
                           bookingFormData.paymentType === "Cash" ? "CASH" : "PAY_LATER";

      // Check if this is a fleet user - use fleet API
      if (isFleetUser()) {
        console.log('🚛 [Manual Search] Fleet user detected - using fleet candidates API');
        
        // For fleet users, get hospitalId from bookingFormData (selected hospital partner)
        const hospitalId = bookingFormData?.hospitalPartnerId;
        
        if (!hospitalId) {
          console.error('❌ [Manual Search] Hospital ID not found for fleet user. Available sources:', {
            hospitalPartnerId: bookingFormData?.hospitalPartnerId,
            bookingFormDataKeys: bookingFormData ? Object.keys(bookingFormData) : []
          });
          throw new Error('Hospital ID not found. Please select a hospital partner before searching for ambulances.');
        }

        // Prepare fleet candidates request data
        const fleetCandidatesData = {
          hospitalId: hospitalId,
          pickupLocation: {
            address: bookingFormData.pickupLocation || "",
            latitude: pickupCoordinates.latitude,
            longitude: pickupCoordinates.longitude
          },
          dropoffLocation: {
            address: bookingFormData.dropLocation || "",
            latitude: dropCoordinates.latitude,
            longitude: dropCoordinates.longitude
          },
          distance_range_km: kmRange || 6, // Default to 6 if not set
          payment_method: paymentMethod
        };

        // Add ambulance type IDs if available
        if (selectedAmbulanceTypes.length > 0) {
          fleetCandidatesData.ambulance_type_ids = selectedAmbulanceTypes;
        } else if (selectedAmbulance?.type_id) {
          fleetCandidatesData.ambulance_type_ids = [selectedAmbulance.type_id];
        } else if (availableAmbulanceTypes.length > 0) {
          const typeIds = availableAmbulanceTypes.map(type => type.type_id).filter(Boolean);
          if (typeIds.length > 0) {
            fleetCandidatesData.ambulance_type_ids = typeIds;
          }
        }

        console.log('🔍 [Manual Search] Fetching fleet candidates with payload:', JSON.stringify(fleetCandidatesData, null, 2));

        const response = await authService.fleetGetCandidates(fleetCandidatesData);
        
        console.log('📥 [Manual Search] Fleet API Response:', {
          success: response.success,
          hasData: !!response.data,
          hasCandidates: !!response.data?.candidates,
          candidatesCount: response.data?.candidates?.length || 0,
          responseKeys: response.data ? Object.keys(response.data) : [],
          fullResponse: response
        });
        
        if (response.success && response.data?.candidates) {
          // Transform candidates to match ambulance UI format
          const transformedCandidates = response.data.candidates.map(candidate => ({
            vehicle_id: candidate.vehicle_id,
            type_id: candidate.type?.id,
            name: candidate.type?.name,
            plate_number: candidate.plate_number,
            driver: {
              id: candidate.driver?.id,
              name: candidate.driver?.name,
              phone: candidate.driver?.phone,
              rating: candidate.driver?.rating,
              total_rides: candidate.driver?.total_rides
            },
            operator: {
              id: candidate.operator?.id,
              name: candidate.operator?.name,
              is_partner: candidate.operator?.is_partner
            },
            eta_minutes: candidate.eta_minutes,
            distanceFromPickup: candidate.distance_from_pickup_km,
            estimated_fare: candidate.estimated_fare,
            is_partner: candidate.operator?.is_partner || false,
            operator_name: candidate.operator?.name,
            pricing_source: candidate.pricing_source ?? null,
            pricing_model: candidate.pricing_model ?? null,
            pricing_breakdown: candidate.pricing_breakdown ?? candidate.breakdown ?? null
          }));

          console.log('✅ [Manual Search] Fleet candidates received:', transformedCandidates.length);
          
          // Filter candidates based on distance if kmRange is set
          let filteredCandidates = transformedCandidates;
          if (kmRange && kmRange < 50) {
            filteredCandidates = filteredCandidates.filter(candidate => 
              candidate.distanceFromPickup <= kmRange
            );
          }

          // Filter by ambulance type if selected
          if (selectedAmbulanceTypes.length > 0) {
            filteredCandidates = filteredCandidates.filter(candidate =>
              selectedAmbulanceTypes.includes(candidate.type_id)
            );
          }

          if (filteredCandidates.length === 0 && transformedCandidates.length > 0) {
            console.warn('⚠️ [Manual Search] All candidates were filtered out. Original count:', transformedCandidates.length);
            setCandidatesError('No ambulances match your selected filters. Please try adjusting your distance or ambulance type filters.');
          } else {
            setCandidates(filteredCandidates);
            setCandidatesError(null);
          }
        } else {
          if (response.success && response.data) {
            console.warn('⚠️ [Manual Search] Fleet API returned success but no candidates array:', response.data);
            setCandidatesError('No ambulances available matching your criteria. Please try adjusting your filters.');
          } else {
            console.error('❌ [Manual Search] Fleet API returned unsuccessful response:', response);
            setCandidatesError(response.message || 'No candidates found. Please try adjusting your filters.');
          }
          setCandidates([]);
        }
      } else {
        // Hospital user flow - use existing hospital API
        const hospitalId = getHospitalIdFromUser();
        console.log('🏥 [Manual Search] Hospital user detected - using hospital candidates API');
        console.log('🏥 [Manual Search] Hospital ID retrieved:', hospitalId);
        console.log('🏥 [Manual Search] User object:', user);
        
        if (!hospitalId) {
          console.error('❌ [Manual Search] Hospital ID not found. Available sources:', {
            userHospitalId: user?.hospital_id,
            userHospitalIdCamel: user?.hospitalId,
            userHospital: user?.hospital,
            localStorageKeys: typeof window !== 'undefined' ? {
              hospitalId: localStorage.getItem(API_CONFIG.STORAGE_KEYS.HOSPITAL_ID),
              hospitalData: localStorage.getItem(API_CONFIG.STORAGE_KEYS.HOSPITAL_DATA),
              userData: localStorage.getItem(API_CONFIG.STORAGE_KEYS.USER_DATA)
            } : 'N/A (server-side)'
          });
          throw new Error('Hospital ID not found. Please ensure you are logged in as a hospital user.');
        }

        // Prepare candidates request data with filters
        const candidatesData = {
          hospitalId: hospitalId,
          pickupLocation: {
            address: bookingFormData.pickupLocation || "",
            latitude: pickupCoordinates.latitude,
            longitude: pickupCoordinates.longitude
          },
          dropoffLocation: {
            address: bookingFormData.dropLocation || "",
            latitude: dropCoordinates.latitude,
            longitude: dropCoordinates.longitude
          },
          distance_range_km: kmRange, // Use filter value
          payment_method: paymentMethod
        };

      // Apply filters based on user selection
      // Rule: If partner is "all" AND no ambulance types selected, return all (don't add filters)
      // Otherwise, apply strict filters
      const isAllPartners = selectedPartner === "all" || selectedPartner === "all_partners";
      const hasAmbulanceTypeFilter = selectedAmbulanceTypes.length > 0;
      
      if (isAllPartners && !hasAmbulanceTypeFilter) {
        // Return all partners and all ambulance types - don't add any filters
        // Only use distance_range_km if it's less than default (50)
        if (kmRange >= 50) {
          // Use default distance or remove it to get all
          delete candidatesData.distance_range_km;
        }
        // Don't add operator_id or ambulance_type_ids
      } else {
        // Apply strict filters
        // Partner filter
        if (!isAllPartners) {
          // Partner is selected - extract operator_id from selectedPartner
          // selectedPartner format is "operatorId_typeId", we need just the operatorId
          let operatorId = null;
          
          if (typeof selectedPartner === 'string' && selectedPartner !== "all") {
            // Check if selectedPartner contains an underscore (format: "operatorId_typeId")
            if (selectedPartner.includes('_')) {
              // Split by underscore and take the first part (operator_id)
              operatorId = selectedPartner.split('_')[0];
            } else {
              // If no underscore, assume it's already just the operator_id
              operatorId = selectedPartner;
            }
          } else {
            // Fallback to bookingFormData fleetPartnerIds
            operatorId = bookingFormData?.fleetPartnerIds?.[0];
          }
          
          // Validate that operatorId is a valid GUID format before adding
          if (operatorId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(operatorId)) {
            candidatesData.operator_id = operatorId;
          } else {
            console.warn('⚠️ [Manual Search] Invalid operator_id format:', operatorId);
          }
        } else if (bookingFormData?.fleetPartnerIds && bookingFormData.fleetPartnerIds.length > 0) {
          // If "all" is selected but hospital has linked partners, 
          // only include those partners (maintain backward compatibility)
          // Note: This case shouldn't happen if user explicitly selects "all"
          // but keeping for safety
        }

        // Ambulance type filter
        if (hasAmbulanceTypeFilter) {
          // Use selected ambulance types from filter
          candidatesData.ambulance_type_ids = selectedAmbulanceTypes;
        } else if (selectedAmbulance?.type_id) {
          // Fallback to selected ambulance if no filter types selected
          candidatesData.ambulance_type_ids = [selectedAmbulance.type_id];
        } else if (availableAmbulanceTypes.length > 0) {
          // Fallback to available types if no specific selection
          const typeIds = availableAmbulanceTypes.map(type => type.type_id).filter(Boolean);
          if (typeIds.length > 0) {
            candidatesData.ambulance_type_ids = typeIds;
          }
        }
      }

      console.log('🔍 [Manual Search] Fetching candidates with payload:', JSON.stringify(candidatesData, null, 2));

      const response = await hospitalService.getCandidates(candidatesData);
      
      console.log('📥 [Manual Search] API Response:', {
        success: response.success,
        hasData: !!response.data,
        hasCandidates: !!response.data?.candidates,
        candidatesCount: response.data?.candidates?.length || 0,
        responseKeys: response.data ? Object.keys(response.data) : [],
        fullResponse: response
      });
      
      if (response.success && response.data?.candidates) {
        // Transform candidates to match ambulance UI format
        const transformedCandidates = response.data.candidates.map(candidate => ({
          vehicle_id: candidate.vehicle_id,
          type_id: candidate.type?.id,
          name: candidate.type?.name,
          plate_number: candidate.plate_number,
          driver: {
            id: candidate.driver?.id,
            name: candidate.driver?.name,
            phone: candidate.driver?.phone,
            rating: candidate.driver?.rating,
            total_rides: candidate.driver?.total_rides
          },
          operator: {
            id: candidate.operator?.id,
            name: candidate.operator?.name,
            is_partner: candidate.operator?.is_partner
          },
          eta_minutes: candidate.eta_minutes,
          distanceFromPickup: candidate.distance_from_pickup_km,
          estimated_fare: candidate.estimated_fare,
          is_partner: candidate.operator?.is_partner || false,
          operator_name: candidate.operator?.name,
          pricing_source: candidate.pricing_source ?? null,
          pricing_model: candidate.pricing_model ?? null,
          pricing_breakdown: candidate.pricing_breakdown ?? candidate.breakdown ?? null
        }));

        console.log('✅ [Manual Search] Candidates received:', transformedCandidates.length);
        
        // Filter candidates based on selected filters
        let filteredCandidates = transformedCandidates;
        
        // Filter by distance if kmRange is set
        if (kmRange && kmRange < 50) {
          filteredCandidates = filteredCandidates.filter(candidate => 
            candidate.distanceFromPickup <= kmRange
          );
        }
        
        // Filter by ambulance type if selected
        if (selectedAmbulanceTypes.length > 0) {
          filteredCandidates = filteredCandidates.filter(candidate =>
            selectedAmbulanceTypes.includes(candidate.type_id)
          );
        }
        
        // Filter by partner if selected (not "all" or "all_partners")
        if (selectedPartner !== "all" && selectedPartner !== "all_partners") {
          // Extract operator_id from selectedPartner (format: "operatorId_typeId")
          let partnerOperatorId = null;
          if (typeof selectedPartner === 'string' && selectedPartner.includes('_')) {
            partnerOperatorId = selectedPartner.split('_')[0];
          } else {
            partnerOperatorId = selectedPartner;
          }

          filteredCandidates = filteredCandidates.filter(candidate => {
            const candidateOperatorId = candidate.operator?.id || candidate.operator_id;
            return String(candidateOperatorId) === String(partnerOperatorId);
          });
        }

        console.log('✅ [Manual Search] Filtered candidates:', filteredCandidates.length, {
          originalCount: transformedCandidates.length,
          afterDistanceFilter: kmRange && kmRange < 50 ? 'applied' : 'not applied',
          afterTypeFilter: selectedAmbulanceTypes.length > 0 ? 'applied' : 'not applied',
          afterPartnerFilter: (selectedPartner !== "all" && selectedPartner !== "all_partners") ? 'applied' : 'not applied'
        });
        
        if (filteredCandidates.length === 0 && transformedCandidates.length > 0) {
          console.warn('⚠️ [Manual Search] All candidates were filtered out. Original count:', transformedCandidates.length);
          setCandidatesError('No ambulances match your selected filters. Please try adjusting your partner, distance, or ambulance type filters.');
        } else {
          setCandidates(filteredCandidates);
          setCandidatesError(null);
        }
      } else {
        // Check if response has data but no candidates
        if (response.success && response.data) {
          console.warn('⚠️ [Manual Search] API returned success but no candidates array:', response.data);
          setCandidatesError('No ambulances available matching your criteria. Please try adjusting your filters.');
        } else {
          console.error('❌ [Manual Search] API returned unsuccessful response:', response);
          setCandidatesError(response.message || 'No candidates found. Please try adjusting your filters.');
        }
        setCandidates([]);
      }
      } // End of hospital user flow - closes the else block for hospital users
    } catch (error) {
      console.error('❌ [Manual Search] Error fetching candidates:', {
        error,
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      setCandidatesError(
        error.response?.data?.message || 
        error.message || 
        'Failed to fetch candidates. Please check your filters and try again.'
      );
      setCandidates([]);
    } finally {
      setCandidatesLoading(false);
    }
  };

  // Helper: Determine payment method
  const determinePaymentMethod = (paymentType) => {
    if (paymentType === "Online") {
      return "ONLINE";
    }
    if (paymentType === "Cash") {
      return "CASH";
    }
    return "PAY_LATER";
  };

  // Helper: Get backend fare estimate only
  const calculateEstimatedFare = (ambulance, fareEstimates) => {
    // ❌ REMOVED: Frontend fallback calculation - Only use backend estimates
    const fareFromState = ambulance?.type_id && fareEstimates[ambulance.type_id];
    return fareFromState || null; // Return null if no backend estimate available
  };

  // Helper: Build scheduled date time
  const buildScheduledDateTime = (bookingType, date, time) => {
    if (bookingType === "scheduled" && date && time) {
      return new Date(`${date}T${time}`).toISOString();
    }
    return undefined;
  };

  // Helper: Build booking data object
  const buildBookingData = (hospitalId, bookingFormData, selectedAmbulance, pickupCoordinates, dropCoordinates, fareEstimates) => {
    const calculatedFare = calculateEstimatedFare(selectedAmbulance, fareEstimates);
    
    // Determine allocation type - STRICTLY enforce MANUAL if allocationType is "Manual"
    const allocationType = bookingFormData.allocationType === "Manual" ? "MANUAL" : 
                          (bookingFormData.allocationType === "Automatic" ? "AUTOMATIC" : undefined);
    
    const bookingData = {
      ...(hospitalId && user?.role !== "TENANT_ADMIN" && { hospitalId: hospitalId }),
      patientName: bookingFormData.name,
      patientAge: 30,
      patientGender: "OTHER",
      pickupLocation: {
        address: bookingFormData.pickupLocation,
        latitude: pickupCoordinates.latitude,
        longitude: pickupCoordinates.longitude,
      },
      dropoffLocation: {
        address: bookingFormData.dropLocation,
        latitude: dropCoordinates.latitude,
        longitude: dropCoordinates.longitude,
      },
      emergencyType: "NON_URGENT",
      priority: "NORMAL",
      bookingType: bookingFormData.bookingType === "scheduled" ? "SCHEDULED" : "INSTANT",
      scheduledDateTime: buildScheduledDateTime(
        bookingFormData.bookingType,
        bookingFormData.date,
        bookingFormData.time
      ),
      notes: `Journey Direction: ${bookingFormData.direction}.`,
      ambulanceType: selectedAmbulance.type_id,
      journeyDirection: bookingFormData.direction.toUpperCase(),
      paymentMethod: determinePaymentMethod(bookingFormData.paymentType),
    };

    // Set allocation type if specified (will be overridden later if needed, but set it here)
    if (allocationType) {
      bookingData.allocationType = allocationType;
    }

    if (bookingFormData?.fleetPartnerIds?.length > 0) {
      bookingData.fleetPartnerIds = bookingFormData.fleetPartnerIds;
    }
    if (bookingFormData?.preferredFleetId) {
      bookingData.preferredFleetId = bookingFormData.preferredFleetId;
    }
    if (calculatedFare && calculatedFare > 0) {
      bookingData.estimatedFare = calculatedFare;
    }

    // Add online payment details if payment method is Online
    if (bookingData.paymentMethod === "ONLINE") {
      bookingData.paymentDetails = {
        paymentGateway: "razorpay", // Default payment gateway
        currency: "INR", // Default currency
        amount: calculatedFare || null, // Estimated fare amount
        paymentMethod: "ONLINE",
        // Include patient contact info for payment gateway prefill
        patientPhone: bookingFormData.phone || null,
        patientEmail: bookingFormData.email || null,
        patientName: bookingFormData.name || null
      };
      
      console.log('💳 [buildBookingData] Added online payment details to booking data:', bookingData.paymentDetails);
    }

    return bookingData;
  };

  // Helper: Add contact information to booking data
  const addContactInfo = (bookingData, bookingFormData) => {
    if (bookingFormData.phone?.trim()) {
      bookingData.patientPhone = bookingFormData.phone.trim();
      bookingData.patientPhone_country_code = "+91";
    }
    if (bookingFormData.email?.trim()) {
      bookingData.patientEmail = bookingFormData.email.trim();
    }
  };

  // Helper: Extract booking ID from response
  const extractBookingId = (result) => {
    return result?.data?.booking_id ||
           result?.data?.id ||
           result?.booking_id ||
           result?.id ||
           null;
  };



  // Helper: Extract driver matching info
  const extractDriverInfo = (result) => {
    const driverMatching = result?.data?.driver_matching;
    const assignedDriver = driverMatching?.assigned_driver || result?.data?.driver || result?.data?.vehicle?.assigned_driver;
    const vehicle = result?.data?.vehicle;
    const operator = assignedDriver?.operator || vehicle?.operator || result?.data?.operator;
    
    // Extract driver phone from multiple possible locations
    const driverPhone = assignedDriver?.phone || 
                       assignedDriver?.contact_phone || 
                       assignedDriver?.phone_number ||
                       assignedDriver?.mobile ||
                       result?.data?.driver?.phone ||
                       result?.data?.driver?.contact_phone ||
                       result?.data?.driver?.phone_number ||
                       result?.data?.driver?.mobile ||
                       driverMatching?.assigned_driver?.phone ||
                       driverMatching?.assigned_driver?.contact_phone ||
                       driverMatching?.assigned_driver?.phone_number ||
                       '';
    
    return {
      driverMatching,
      driverStatus: driverMatching?.success 
        ? driverMatching?.message || 'Driver assigned'
        : 'Waiting for driver assignment...',
      matchingStatus: driverMatching?.success ? 'assigned' : 'searching',
      driverName: assignedDriver?.driver_name || assignedDriver?.name || driverMatching?.assigned_driver?.driver_name || result?.data?.driver?.name,
      driverPhone: driverPhone,
      driverId: assignedDriver?.driver_id || assignedDriver?.id || result?.data?.driver?.driver_id,
      vendorName: operator?.name || 
                 operator?.operator_name || 
                 operator?.fleet_name ||
                 operator?.company_name ||
                 vehicle?.operator_name || 
                 vehicle?.operator?.name ||
                 vehicle?.fleet_name ||
                 result?.data?.operator?.name ||
                 result?.data?.operator?.operator_name ||
                 result?.data?.fleet?.name ||
                 result?.data?.fleet_name ||
                 '',
      vendorContact: (() => {
        const contact = operator?.contact_info || 
                      operator?.phone || 
                      operator?.contact_phone ||
                      operator?.mobile ||
                      operator?.mobile_number ||
                      operator?.contact_number ||
                      operator?.fleet?.phone ||
                      operator?.fleet?.contact_phone ||
                      vehicle?.operator?.phone ||
                      vehicle?.operator?.contact_phone ||
                      vehicle?.operator?.contact_info ||
                      vehicle?.fleet?.phone ||
                      vehicle?.fleet?.contact_phone ||
                      result?.data?.operator?.contact_info ||
                      result?.data?.operator?.phone ||
                      result?.data?.operator?.contact_phone ||
                      result?.data?.operator?.mobile ||
                      result?.data?.fleet?.phone ||
                      result?.data?.fleet?.contact_phone ||
                      result?.data?.fleet?.mobile ||
                      result?.data?.vehicle?.operator?.phone ||
                      result?.data?.vehicle?.operator?.contact_phone ||
                      result?.data?.vehicle?.fleet?.phone ||
                      '';
        
        console.log('📞 [AmbulanceInstantBookingDuplicate] Vendor contact extracted:', {
          contact,
          operator: operator,
          operatorPhone: operator?.phone,
          operatorContactInfo: operator?.contact_info,
          operatorContactPhone: operator?.contact_phone,
          vehicle: vehicle,
          vehicleOperator: vehicle?.operator,
          resultDataOperator: result?.data?.operator,
          resultDataFleet: result?.data?.fleet,
          fullResult: result?.data
        });
        
        return contact;
      })(),
      vehicleNumber: vehicle?.vehicle_number || vehicle?.registration_number || vehicle?.ambulance_number || result?.data?.vehicle?.vehicle_number,
    };
  };

  // Helper: Build success modal data
  const buildSuccessModalData = (bookingId, bookingFormData, selectedAmbulance, finalFare, driverInfo) => {
    // Build scheduled date time for display
    let scheduledDateTimeForDisplay = undefined;
    if (bookingFormData.bookingType === "scheduled" && bookingFormData.date && bookingFormData.time) {
      scheduledDateTimeForDisplay = new Date(`${bookingFormData.date}T${bookingFormData.time}`).toISOString();
    }
    
    const modalData = {
      bookingId: bookingId,
      patientName: bookingFormData.name,
      hospitalName: "Selected Hospital",
      ambulanceType: selectedAmbulance?.name || "Ambulance",
      estimatedFare: finalFare,
      finalFare: finalFare,
      driverStatus: driverInfo.driverStatus,
      driverName: driverInfo.driverName,
      driverPhone: driverInfo.driverPhone,
      driverId: driverInfo.driverId,
      vendorName: driverInfo.vendorName,
      vendorContact: driverInfo.vendorContact,
      vehicleNumber: driverInfo.vehicleNumber,
      pickupAddress: bookingFormData.pickupLocation,
      destinationAddress: bookingFormData.dropLocation,
      bookingType: bookingFormData.bookingType === "scheduled" ? "SCHEDULED" : (bookingFormData.bookingType || "INSTANT"),
      direction: bookingFormData.direction,
      matchingStatus: driverInfo.matchingStatus,
      scheduledDateTime: scheduledDateTimeForDisplay,
      scheduledDate: bookingFormData.date,
      scheduledTime: bookingFormData.time,
    };
    
    // Debug: Log what's being passed to success modal
    console.log('📋 [AmbulanceInstantBookingDuplicate] Building success modal data:', {
      vendorName: modalData.vendorName,
      vendorContact: modalData.vendorContact,
      driverInfo: driverInfo,
      bookingType: modalData.bookingType,
      scheduledDateTime: modalData.scheduledDateTime,
      fullModalData: modalData
    });
    
    return modalData;
  };

  // Helper: Check if should show open others modal
  const shouldShowOpenOthersModal = (bookingFormData, driverMatching) => {
    const hadPreferred = !!bookingFormData?.preferredFleetId;
    const hadFleetList = Array.isArray(bookingFormData?.fleetPartnerIds) && 
                         bookingFormData.fleetPartnerIds.length > 0;
    return hadPreferred && hadFleetList && !driverMatching?.success;
  };

  // Helper: Process successful booking
  const processSuccessfulBooking = (bookingId, bookingData, result, bookingFormData, selectedAmbulance, fareEstimates) => {
    const finalFare = calculateEstimatedFare(selectedAmbulance, fareEstimates) || 0;
    const driverInfo = extractDriverInfo(result);

    // Debug: Log payment type to verify it's being passed correctly
    console.log('💳 [AmbulanceInstantBookingDuplicate] Processing successful booking:', {
      bookingId,
      paymentType: bookingFormData.paymentType,
      isOnlinePayment: bookingFormData.paymentType === "Online",
      isFleetUser: isFleetUser(),
      isHospitalUser: isHospitalUser()
    });

    // Check if payment type is "Online" - if so, open payment modal instead of success modal
    if (bookingFormData.paymentType === "Online") {
      // For fleet users, open PaymentOrderCreationModal directly
      // For hospital users, open PaymentInitiationModal
      if (isFleetUser()) {
        console.log('🚛 [AmbulanceInstantBookingDuplicate] Fleet user detected - opening PaymentOrderCreationModal');
        setPaymentOrderModal({
          isOpen: true,
          bookingId: bookingId,
          bookingData: {
            estimated_fare: finalFare,
            estimatedFare: finalFare,
            final_fare: finalFare,
            finalFare: finalFare,
            fare: finalFare,
            amount: finalFare,
          },
          estimatedFare: finalFare,
        });

        // Store booking data for later use after payment success
        setSuccessModal({
          isOpen: false,
          data: {
            ...buildSuccessModalData(bookingId, bookingFormData, selectedAmbulance, finalFare, driverInfo),
            pendingPayment: true,
          }
        });
      } else {
        // Hospital users - use PaymentInitiationModal
        console.log('🏥 [AmbulanceInstantBookingDuplicate] Hospital user detected - opening PaymentInitiationModal');
        setPaymentModal({
          isOpen: true,
          bookingId: bookingId,
          bookingData: {
            patientPhone: bookingFormData.phone,
            patientEmail: bookingFormData.email,
          },
          estimatedFare: finalFare,
          patientName: bookingFormData.name,
          pickupAddress: bookingFormData.pickupLocation,
          destinationAddress: bookingFormData.dropLocation,
          ambulanceType: selectedAmbulance?.name || "Ambulance",
        });

        // Store booking data for later use after payment success
        setSuccessModal({
          isOpen: false,
          data: {
            ...buildSuccessModalData(bookingId, bookingFormData, selectedAmbulance, finalFare, driverInfo),
            pendingPayment: true,
          }
        });
      }
    } else {
      // For cash or other payment types, show success modal directly
      setSuccessModal({
        isOpen: true,
        data: buildSuccessModalData(bookingId, bookingFormData, selectedAmbulance, finalFare, driverInfo)
      });
    }

    if (shouldShowOpenOthersModal(bookingFormData, driverInfo.driverMatching)) {
      setOpenOthersModal({
        isOpen: true,
        preferredFleetName: null,
        context: { bookingId, fleetPartnerIds: bookingFormData.fleetPartnerIds },
      });
    }

    onBookingChange({
      bookingId,
      ...bookingData,
      success: true,
      result,
    });
  };

  // Handle booking creation for manual allocation (skips confirmation modal)
  const handleConfirmBookingForManual = async (currentFormData, ambulance) => {
    setIsLoading(true);
    setShowLoadingModal(true);

    try {
      validateBookingPrerequisites();

      let bookingId;
      let result;
      
      // CRITICAL: Check if booking was already created via startManualAllocation
      // If currentBookingId exists, DON'T create a new booking (prevents duplicates)
      if (currentBookingId) {
        console.log('✅ [Manual Booking] Booking already exists from manual allocation:', currentBookingId);
        console.log('✅ [Manual Booking] Skipping booking creation to prevent duplicate');
        console.log('✅ [Manual Booking] Driver was already assigned via manual allocation flow');
        
        bookingId = currentBookingId;
        
        // Create a mock result object with the existing booking ID
        result = {
          success: true,
          data: {
            booking_id: bookingId,
            bookingId: bookingId,
            id: bookingId
          }
        };
      } else {
        // No existing booking - this shouldn't normally happen in manual allocation flow
        // but handle it just in case
        console.warn('⚠️ [Manual Booking] No existing bookingId found - creating new booking');
        console.warn('⚠️ [Manual Booking] This should not happen in normal manual allocation flow');

        const hospitalId = getHospitalIdFromUser();
        let bookingData = buildBookingData(
          hospitalId,
          currentFormData,
          ambulance,
          pickupCoordinates,
          dropCoordinates,
          fareEstimates
        );

        // STRICTLY set allocation type to MANUAL for manual allocation flow
        bookingData.allocationType = 'MANUAL';

        // Add manual allocation data to booking
        if (currentFormData.manualAllocation?.ambulance) {
          bookingData.vehicle_id = currentFormData.manualAllocation.ambulance.vehicle_id || 
                                   currentFormData.manualAllocation.ambulance.id;
          bookingData.vehicleNumber = currentFormData.manualAllocation.ambulance.vehicleNumber || 
                                       currentFormData.manualAllocation.ambulance.vehicle_number ||
                                       currentFormData.manualAllocation.ambulance.plate_number ||
                                       currentFormData.manualAllocation.ambulance.plateNumber;
        }
        if (currentFormData.manualAllocation?.driver) {
          bookingData.driver_id = currentFormData.manualAllocation.driver.driver_id || 
                                  currentFormData.manualAllocation.driver.id;
          bookingData.driverName = currentFormData.manualAllocation.driver.name || 
                                    currentFormData.manualAllocation.driver.driver_name ||
                                    currentFormData.manualAllocation.driver.driverName;
        }

        addContactInfo(bookingData, currentFormData);
        
        console.log('📋 [Manual Booking] Allocation type set to:', bookingData.allocationType);

        // Create booking
        if (isHospitalUser()) {
          result = await hospitalService.createBooking(bookingData);
        } else if (isFleetUser()) {
          result = await authService.fleetCreateBooking(bookingData);
        } else {
          result = await apiService.createBooking(bookingData);
        }
        
        bookingId = extractBookingId(result);
      }

      if (!bookingId) {
        throw new Error("No booking ID received from server");
      }

      // Build driver info from manual allocation or result
      const finalFare = calculateEstimatedFare(ambulance, fareEstimates) || 0;
      const manualDriverInfo = currentFormData.manualAllocation ? {
        driverName: currentFormData.manualAllocation.driver?.name || 
                   currentFormData.manualAllocation.driver?.driver_name || 
                   currentFormData.manualAllocation.driver?.driverName || 'N/A',
        driverPhone: currentFormData.manualAllocation.driver?.phone || 
                    currentFormData.manualAllocation.driver?.phone_number || 
                    currentFormData.manualAllocation.driver?.contact_phone ||
                    currentFormData.manualAllocation.driver?.mobile ||
                    currentFormData.manualAllocation.driver?.mobile_number ||
                    currentFormData.manualAllocation.driver?.contact_number || 'N/A',
        vehicleNumber: currentFormData.manualAllocation.ambulance?.vehicleNumber || 
                      currentFormData.manualAllocation.ambulance?.vehicle_number ||
                      currentFormData.manualAllocation.ambulance?.plate_number ||
                      currentFormData.manualAllocation.ambulance?.plateNumber || 'N/A',
        driverStatus: 'Driver assigned (Manual)',
        matchingStatus: 'assigned',
        driverMatching: { success: true },
        vendorName: 'N/A',
        vendorContact: 'N/A',
        driverId: currentFormData.manualAllocation.driver?.driver_id || 
                 currentFormData.manualAllocation.driver?.id,
      } : extractDriverInfo(result);
      
      // Debug: Log manual driver info to verify phone number is captured
      if (currentFormData.manualAllocation) {
        console.log('📞 [Manual Allocation] Driver phone number extraction:', {
          driverObject: currentFormData.manualAllocation.driver,
          extractedPhone: manualDriverInfo.driverPhone,
          phoneFields: {
            phone: currentFormData.manualAllocation.driver?.phone,
            phone_number: currentFormData.manualAllocation.driver?.phone_number,
            contact_phone: currentFormData.manualAllocation.driver?.contact_phone,
            mobile: currentFormData.manualAllocation.driver?.mobile,
            mobile_number: currentFormData.manualAllocation.driver?.mobile_number,
            contact_number: currentFormData.manualAllocation.driver?.contact_number,
          }
        });
      }

      // Build success modal data
      const successModalData = buildSuccessModalData(bookingId, currentFormData, ambulance, finalFare, manualDriverInfo);

      // For Online payment, show payment modal first; for Cash, show success modal directly
      if (currentFormData.paymentType === "Online") {
        if (isFleetUser()) {
          setPaymentOrderModal({
            isOpen: true,
            bookingId: bookingId,
            bookingData: {
              estimated_fare: finalFare,
              estimatedFare: finalFare,
              final_fare: finalFare,
              finalFare: finalFare,
              fare: finalFare,
              amount: finalFare,
            },
            estimatedFare: finalFare,
          });
          setSuccessModal({
            isOpen: false,
            data: { ...successModalData, pendingPayment: true },
          });
        } else {
          setPaymentModal({
            isOpen: true,
            bookingId: bookingId,
            bookingData: {
              patientPhone: currentFormData.phone,
              patientEmail: currentFormData.email,
            },
            estimatedFare: finalFare,
            patientName: currentFormData.name,
            pickupAddress: currentFormData.pickupLocation,
            destinationAddress: currentFormData.dropLocation,
            ambulanceType: ambulance?.name || "Ambulance",
          });
          setSuccessModal({
            isOpen: false,
            data: { ...successModalData, pendingPayment: true },
          });
        }
      } else {
        // Cash payment - show success modal directly
        setSuccessModal({
          isOpen: true,
          data: successModalData,
        });
      }

      onBookingChange({
        bookingId,
        ...bookingData,
        success: true,
        result,
      });
    } catch (error) {
      console.error("Manual booking creation failed:", error);
      
      // If validation error modal is already shown, don't show regular error
      if (validationErrorModal.isOpen) {
        setIsLoading(false);
        setShowLoadingModal(false);
        return;
      }
      
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data?.error || 
                          error?.message || 
                          "Failed to create booking. Please try again.";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
      setShowLoadingModal(false);
    }
  };

  // Handle actual booking creation
  const handleConfirmBooking = async () => {
    setIsLoading(true);
    setConfirmationModal({ isOpen: false, data: null });
    setShowLoadingModal(true);

    try {
      validateBookingPrerequisites();

      console.log("🔍 About to prepare booking data. Current bookingFormData:", {
        phone: bookingFormData.phone,
        email: bookingFormData.email,
        name: bookingFormData.name,
        hasPhone: !!bookingFormData.phone,
        hasEmail: !!bookingFormData.email
      });

      const hospitalId = getHospitalIdFromUser();
      let bookingData = buildBookingData(
        hospitalId,
        bookingFormData,
        selectedAmbulance,
        pickupCoordinates,
        dropCoordinates,
        fareEstimates
      );

      addContactInfo(bookingData, bookingFormData);

      console.log("🏥 Hospital ID from user profile:", hospitalId);
      console.log("🏥 User object:", user);
      console.log("💰 Estimated fare being sent:", bookingData.estimatedFare);
      console.log("💰 Selected ambulance:", selectedAmbulance);
      console.log("📦 Creating booking with data:", bookingData);

      // Use appropriate endpoint based on user type
      let result;
      if (isHospitalUser()) {
        console.log("🏥 Using hospital v1 booking API");
        result = await hospitalService.createBooking(bookingData);
      } else if (isFleetUser()) {
        console.log("🚛 Using fleet v1 booking API");
        result = await authService.fleetCreateBooking(bookingData);
      } else {
        console.log("👤 Using general booking API");
        result = await apiService.createBooking(bookingData);
      }
      
      console.log("📦 Booking creation response:", result);
      
      const bookingId = extractBookingId(result);

      if (!bookingId) {
        throw new Error("No booking ID received from server");
      }

      // Show confirmation message / toast
      showSuccess(
        "Booking Confirmed",
        "Booking confirmed successfully"
      );

      processSuccessfulBooking(
        bookingId,
        bookingData,
        result,
        bookingFormData,
        selectedAmbulance,
        fareEstimates
      );

      console.log("Booking created successfully. Smart matching will be handled by backend.");
    } catch (error) {
      console.error("Booking creation failed:", error);
      
      // If validation error modal is already shown, don't show regular error
      if (validationErrorModal.isOpen) {
        // Validation error modal is already displayed, just reset loading state
        setIsLoading(false);
        setShowLoadingModal(false);
        return;
      }
      
      // Extract error message from various possible response structures
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data?.error || 
                          error?.message || 
                          "Failed to create booking. Please try again.";
      
      // Check if it's the specific "no ambulances available" error
      const isAmbulanceUnavailableError = errorMessage.toLowerCase().includes("no ambulances are currently available") ||
                                         errorMessage.toLowerCase().includes("no ambulances available");
      
      if (isAmbulanceUnavailableError) {
        // Show notification that auto-closes after 5 seconds
        showError(
          'No Ambulances Available',
          errorMessage
        );
        // Also clear the error state after showing notification
        setError("");
      } else {
        // For other errors, use the existing error display
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
      setShowLoadingModal(false);
    }
  };

  // Handle track booking navigation
  const handleTrackBooking = () => {
    const bookingId = successModal.data?.bookingId;
    if (!bookingId) return;

    // Close modal
    setSuccessModal({ isOpen: false, data: null });

    const direction = successModal.data?.direction || bookingFormData?.direction || '';
    if (onTrackBookingClick) {
      onTrackBookingClick(bookingId, direction);
    } else {
      navigate(`/track-booking?bookingId=${bookingId}${direction ? `&direction=${direction.toUpperCase()}` : ''}`);
    }
  };

  // Handle success modal close
  const handleSuccessModalClose = () => {
    setSuccessModal({ isOpen: false, data: null });
    // Reset form
    setBookingFormData(null);
    setSelectedAmbulance(null);
    setPickupCoordinates(null);
    setDropCoordinates(null);
    setShowChooseAmbulanceCard(false);
  };

  // Handle open-to-others confirmation
  const handleConfirmOpenToOthers = async () => {
    const { context } = openOthersModal;
    setOpenOthersModal({ isOpen: false, preferredFleetName: null, context: null });
    try {
      if (context?.bookingId && context?.fleetPartnerIds?.length > 0) {
        await apiService.post(`/bookings/${context.bookingId}/open-to-others`, {
          fleetPartnerIds: context.fleetPartnerIds,
        });
      }
    } catch (e) {
      console.warn('Failed to open booking to other fleets:', e);
    }
  };

  const handleCancelOpenToOthers = () => {
    setOpenOthersModal({ isOpen: false, preferredFleetName: null, context: null });
  };

  // Handle payment success - opens verification modal
  const handlePaymentSuccess = (paymentResponse) => {
    console.log('✅ [AmbulanceInstantBookingDuplicate] Payment successful, opening verification modal:', paymentResponse);
    
    // Store bookingId before clearing payment modal state
    const bookingId = paymentModal.bookingId;

    // Close payment initiation modal
    setPaymentModal({
      isOpen: false,
      bookingId: null,
      bookingData: null,
      estimatedFare: null,
      patientName: null,
      pickupAddress: null,
      destinationAddress: null,
      ambulanceType: null,
    });

    // Open payment verification modal with payment details
    setVerificationModal({
      isOpen: true,
      orderId: paymentResponse.orderId,
      paymentId: paymentResponse.paymentId,
      signature: paymentResponse.signature,
      bookingId: bookingId,
    });
  };

  // Handle payment verification success
  const handleVerificationSuccess = (verificationData) => {
    console.log('✅ [AmbulanceInstantBookingDuplicate] Payment verified successfully:', verificationData);
    
    // Close verification modal
    setVerificationModal({
      isOpen: false,
      orderId: null,
      paymentId: null,
      signature: null,
      bookingId: null,
    });

    // Show success modal with payment confirmation
    if (successModal.data) {
      setSuccessModal({
        isOpen: true,
        data: {
          ...successModal.data,
          paymentStatus: 'success',
          paymentId: verificationData.paymentId || verificationModal.paymentId,
          orderId: verificationData.orderId || verificationModal.orderId,
          pendingPayment: false,
        }
      });
    }

    // Update URL with payment info
    const bookingId = verificationData.bookingId || verificationModal.bookingId;
    if (bookingId) {
      const url = new URL(window.location.href);
      url.searchParams.set('paymentStatus', 'success');
      url.searchParams.set('bookingId', bookingId);
      if (verificationData.paymentId) {
        url.searchParams.set('paymentId', verificationData.paymentId);
      }
      window.history.replaceState({}, document.title, url.toString());
    }
  };

  // Handle payment verification failure
  const handleVerificationFailure = (error) => {
    console.error('❌ [AmbulanceInstantBookingDuplicate] Payment verification failed:', error);
    setError(error || 'Payment verification failed. Please contact support.');
  };

  // Handle payment error
  const handlePaymentError = (errorMessage) => {
    console.error('❌ [AmbulanceInstantBookingDuplicate] Payment error:', errorMessage);
    setError(errorMessage || 'Payment failed. Please try again.');
    // Keep payment modal open so user can retry
  };

  const getLayoutClasses = () => {
    switch (layout) {
      case "compact":
        return "p-2 gap-4";
      case "full-width":
        return "p-6 gap-8";
      default:
        return "p-0 gap-6";
    }
  };

  const getContentLayout = () => {
    switch (layout) {
      case "compact":
        return "flex-col sm:flex-row";
      case "full-width":
        return "flex-row";
      default:
        return "flex-col lg:flex-row";
    }
  };

  const getFormLayout = () => {
    switch (layout) {
      case "compact":
        return "flex-col sm:flex-row";
      case "full-width":
        return "flex-row";
      default:
        return "flex-col xl:flex-row";
    }
  };

  // Filter candidates based on search query (UI-based filtering, no API call)
  const filteredCandidates = candidates.filter((candidate) => {
    if (!candidateSearchQuery.trim()) return true;
    const query = candidateSearchQuery.toLowerCase().trim();
    const name = (candidate.name || '').toLowerCase();
    const operatorName = (candidate.operator_name || '').toLowerCase();
    const plateNumber = (candidate.plate_number || '').toLowerCase();
    const driverName = (candidate.driver?.name || '').toLowerCase();
    const driverPhone = (candidate.driver?.phone || '').toLowerCase();
    return (
      name.includes(query) ||
      operatorName.includes(query) ||
      plateNumber.includes(query) ||
      driverName.includes(query) ||
      driverPhone.includes(query)
    );
  });

  // Pricing breakdown overlay (fixed, always visible when open)
  const pricingOverlay = pricingInfoData && (() => {
    const pb = pricingInfoData.pricing_breakdown || {};
    const num = (v) => (v != null && v !== '' && typeof v === 'number' && !Number.isNaN(v) ? `₹${v}` : '—');
    const formatAdjustment = (adj) => {
      if (adj == null || adj === '') return '—';
      if (typeof adj === 'object') {
        const amountNum = Number(adj.amount);
        const amt = !Number.isNaN(amountNum) ? `₹${amountNum}` : null;
        const type = adj.type ?? '';
        const pct = adj.percent != null && adj.percent !== '' ? `${adj.percent}%` : '';
        const parts = [type, pct].filter(Boolean).join(' ');
        if (amt) return parts ? `${parts} • ${amt}` : amt;
        return parts || '—';
      }
      if (typeof adj === 'number' && !Number.isNaN(adj)) return `₹${adj}`;
      return '—';
    };
    const toNum = (v) => (v != null && v !== '' && typeof v === 'number' && !Number.isNaN(v) ? v : 0);
    const adjustmentAmount = (adj) => {
      if (adj == null || adj === '') return 0;
      if (typeof adj === 'object' && adj.amount != null) return toNum(adj.amount);
      if (typeof adj === 'number' && !Number.isNaN(adj)) return adj;
      return 0;
    };
    const adjustmentSigned = (adj) => {
      const amt = adjustmentAmount(adj);
      if (amt === 0) return 0;
      const type = (adj && typeof adj === 'object' && (adj.type ?? adj.adjustmentType ?? '')).toString().toUpperCase();
      return type === 'DISCOUNT' ? -amt : amt;
    };
    const sum =
      toNum(pb.base_fare ?? pb.baseFare) +
      toNum(pb.distance_fare ?? pb.distanceFare) +
      toNum(pb.time_fare ?? pb.timeFare) +
      toNum(pb.driver_charges ?? pb.driverCharges) +
      toNum(pb.taxes) +
      toNum(pb.commission) +
      adjustmentSigned(pb.adjustment);
    const decimal = sum % 1;
    const total = decimal <= 0.4 ? Math.floor(sum) : Math.ceil(sum);

    // Build Total formula like: Total (800 + 152 + 300)
    const totalParts = [
      toNum(pb.base_fare ?? pb.baseFare),
      toNum(pb.distance_fare ?? pb.distanceFare),
      toNum(pb.time_fare ?? pb.timeFare),
      toNum(pb.driver_charges ?? pb.driverCharges),
      toNum(pb.taxes),
      toNum(pb.commission),
      adjustmentSigned(pb.adjustment),
    ].filter((v) => typeof v === 'number' && !Number.isNaN(v) && v !== 0);

    const totalFormula = totalParts.reduce((acc, v, idx) => {
      const abs = Math.abs(v);
      if (idx === 0) return `${abs}`;
      return v < 0 ? `${acc} - ${abs}` : `${acc} + ${abs}`;
    }, '');
    return createPortal(
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60"
        onClick={() => setPricingInfoData(null)}
        role="dialog"
        aria-label="Pricing breakdown"
      >
        <div
          className="w-full max-w-sm rounded-xl border border-gray-500 bg-gray-900 shadow-2xl p-4 text-left"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="text-base font-semibold text-white mb-3 border-b border-gray-600 pb-2">Pricing breakdown</div>
          <div className="space-y-2 text-sm text-gray-300">
            <div className="flex justify-between gap-4"><span className="text-gray-400">Source</span><span className="font-medium text-gray-200">{pricingInfoData.pricing_source ?? '—'}</span></div>
            <div className="flex justify-between gap-4"><span className="text-gray-400">Model</span><span className="font-medium text-gray-200">{pricingInfoData.pricing_model ?? '—'}</span></div>
            <div className="flex justify-between gap-4"><span className="text-gray-400">Base fare</span><span>{num(pb.base_fare ?? pb.baseFare)}</span></div>
            <div className="flex justify-between gap-4"><span className="text-gray-400">Distance fare</span><span>{num(pb.distance_fare ?? pb.distanceFare)}</span></div>
            <div className="flex justify-between gap-4"><span className="text-gray-400">Time fare</span><span>{num(pb.time_fare ?? pb.timeFare)}</span></div>
            <div className="flex justify-between gap-4"><span className="text-gray-400">Driver charges</span><span>{num(pb.driver_charges ?? pb.driverCharges)}</span></div>
            <div className="flex justify-between gap-4"><span className="text-gray-400">Taxes</span><span>{num(pb.taxes)}</span></div>
            <div className="flex justify-between gap-4"><span className="text-gray-400">Commission</span><span>{num(pb.commission)}</span></div>
            <div className="flex justify-between gap-4"><span className="text-gray-400">Adjustment</span><span>{formatAdjustment(pb.adjustment)}</span></div>
            <div className="flex justify-between gap-4 pt-2 mt-2 border-t border-gray-600">
              <span className="text-gray-300 font-medium">
                Total{totalFormula ? ` (${totalFormula})` : ''}
              </span>
              <span className="font-semibold text-white">₹{total}</span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setPricingInfoData(null)}
            className="mt-3 w-full py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>,
      document.body
    );
  })();

  // Stable callback so child only notifies when ambulance types actually change, not on every parent re-render (e.g. timer)
  const handleAmbulanceTypesChange = useCallback((types) => {
    if (types.length === 0) {
      pendingAmbulanceTypesRef.current = [];
      setAllAmbulanceTypes([]);
      const clearAvailableTypes = () => {
        if (autoAllocationConfirmed && allocationType === "Automatic") {
          setAvailableAmbulanceTypes([]);
          return true;
        }
        return false;
      };
      if (!clearAvailableTypes()) {
        setTimeout(() => {
          if (!clearAvailableTypes()) {
            setTimeout(() => clearAvailableTypes(), 100);
          }
        }, 150);
      }
      return;
    }
    setAllAmbulanceTypes(types);
    if (types.length > 0) {
      pendingAmbulanceTypesRef.current = types;
    }
    const updateTypes = () => {
      if (autoAllocationConfirmed && allocationType === "Automatic" && types.length > 0) {
        setAvailableAmbulanceTypes(types);
        return true;
      }
      return false;
    };
    if (!updateTypes() && types.length > 0) {
      setTimeout(() => {
        if (pendingAmbulanceTypesRef.current.length > 0) {
          const shouldUpdate = autoAllocationConfirmed && allocationType === "Automatic";
          if (shouldUpdate) {
            setAvailableAmbulanceTypes(pendingAmbulanceTypesRef.current);
          }
        }
      }, 150);
    }
  }, [autoAllocationConfirmed, allocationType]);

  return (
    <>
      {pricingOverlay}
      <div className={`relative min-h-screen w-full ${className}`}>
      {/* Main Content Area */}
      <div
        className={`relative z-10 flex ${getContentLayout()} items-start justify-between ${getLayoutClasses()} min-h-screen ${contentClassName}`}
      >
        {/* Left Side - Booking Forms */}
        {(showGetAmbulanceCard || showChooseAmbulance) && (
          <div className={`flex ${getFormLayout()} gap-6 flex-1 relative z-20`}>
            {showGetAmbulanceCard && (
              <GetAmbulanceCard
                onBookingChange={onBookingChange}
                selectedAmbulance={selectedAmbulance}
                triggerBooking={handleBookingButtonClick}
                onPickupCoordinatesChange={setPickupCoordinates}
                onDropCoordinatesChange={setDropCoordinates}
                fareEstimates={fareEstimates}
                onShowChooseAmbulance={() => setShowChooseAmbulanceCard(true)}
                onFormDataChange={handleFormData}
                showMapPicker={true}
                showCurrentLocation={true}
              />
            )}
            {showChooseAmbulance && showChooseAmbulanceCard && (
              <ChooseAmbulanceDuplicate
                key={`choose-ambulance-${allocationResetCounter}`}
                selectedAmbulance={selectedAmbulance}
                pickupCoordinates={pickupCoordinates}
                dropCoordinates={dropCoordinates}
                onAmbulanceSelect={handleAmbulanceSelect}
                onConfirmSelection={handleConfirmSelection}
                onFareEstimatesChange={(estimates) => {
                  console.log("📥 [Parent] Received fare estimates:", estimates);
                  setFareEstimates(estimates);
                }}
                onVendorNamesChange={(vendorNames) => {
                  console.log("📥 [Parent] Received vendor names:", vendorNames);
                  setVendorNamesMap(vendorNames);
                }}
                journeyDirection={bookingFormData?.direction?.toUpperCase()}
                bookingFormData={bookingFormData}
                triggerBooking={handleBookingButtonClick}
                onAllocationTypeChange={setAllocationType}
                onAmbulanceTypesChange={handleAmbulanceTypesChange}
                onStartAutoAssignment={(bookingId, initialAllocationData) => {
                  setAutoAllocationConfirmed(true); // Mark that auto-allocation has been confirmed
                  
                  // Store booking_id if provided
                  if (bookingId) {
                    // Only set shouldStartAutoAssignment to true if we have a bookingId (meaning candidates were found)
                    setShouldStartAutoAssignment(true);
                    setCurrentBookingId(bookingId);
                    // Set initial allocation status from startAutoAllocation API response
                    // This provides immediate data before polling starts
                    if (initialAllocationData) {
                      setAllocationStatus({
                        booking_id: bookingId,
                        response_id: initialAllocationData.response_id,
                        allocation_type: 'AUTOMATIC',
                        ui_state: initialAllocationData.status,
                        driver_response: {
                          status: initialAllocationData.status,
                          expires_in_sec: initialAllocationData.expires_in_sec,
                          responded_at: null
                        },
                        driver: initialAllocationData.driver,
                        vehicle: initialAllocationData.vehicle,
                        estimated_fare: initialAllocationData.estimated_fare,
                        current_driver_id: initialAllocationData.current_driver_id
                      });
                    }
                    
                    // For fleet users in automatic flow, open the automatic assignment popup with bookingId
                    // This ensures the modal is displayed and starts polling immediately
                    if (isFleetUser() && allocationType === "Automatic") {
                      console.log("🚛 [Parent] Fleet automatic flow - opening automatic assignment popup with bookingId:", bookingId);
                      // Get the first available ambulance to display in the modal
                      const firstAmbulance = availableAmbulanceTypes.length > 0 ? availableAmbulanceTypes[0] : null;
                      if (firstAmbulance) {
                        const modalDriver = firstAmbulance?.driver ? {
                          name: firstAmbulance.driver.name || 'N/A',
                          phone: firstAmbulance.driver.phone || 'N/A',
                          rating: firstAmbulance.driver.rating || null,
                          ambulanceNumber: firstAmbulance.plate_number || null
                        } : null;
                        
                        setAutomaticAssignmentPopup({
                          isOpen: true,
                          ambulance: firstAmbulance,
                          driver: modalDriver,
                          timer: 30,
                          bookingId: bookingId
                        });
                        console.log("✅ [Parent] Automatic assignment popup opened for fleet automatic flow");
                      }
                    }
                    
                    // Start polling allocation status for automatic allocation
                    // This ensures real-time driver/vehicle data is continuously updated
                    startPollingAllocationStatus(bookingId);
                  } else {
                    console.log("✅ [Parent] Auto-allocation confirmed (no bookingId) - candidates === 0 scenario");
                    // When no bookingId is provided, it means candidates === 0
                    // Clear availableAmbulanceTypes immediately to show "no available" message
                    // Use setTimeout to ensure state update happens after confirmation is set
                    setTimeout(() => {
                      console.log("📥 [Parent] Clearing availableAmbulanceTypes to show no candidates modal");
                      setAvailableAmbulanceTypes([]);
                      // Also clear pending types ref to prevent any race conditions
                      pendingAmbulanceTypesRef.current = [];
                    }, 10);
                  }
                  
                  // If we have pending types stored in ref, apply them now
                  if (pendingAmbulanceTypesRef.current.length > 0 && allocationType === "Automatic") {
                    console.log("✅ [Parent] Applying pending ambulance types after confirmation");
                    setAvailableAmbulanceTypes(pendingAmbulanceTypesRef.current);
                  }
                }}
                onOpenManualAllocationModal={(openFn) => {
                  openManualAllocationModalRef.current = openFn;
                }}
                onManualSearch={handleManualSearch}
                allocationStatus={allocationStatus}
                bookingId={currentBookingId}
                isPollingAllocation={isPollingAllocation}
                onRefetchCandidates={handleRefetchCandidatesWithBookingId}
                onRefetchAllocationStatus={() => {
                  // WebSocket-triggered refetch: Call pollAllocationStatus if bookingId exists
                  if (currentBookingId) {
                    console.log('🔄 [Parent] WebSocket triggered refetch for booking:', currentBookingId);
                    pollAllocationStatus(currentBookingId);
                  }
                }}
                onStopAllocation={() => {
                  console.log('🛑 [Parent] Stopping manual allocation process');
                  // Stop polling
                  if (allocationPollingIntervalRef.current) {
                    clearInterval(allocationPollingIntervalRef.current);
                    allocationPollingIntervalRef.current = null;
                  }
                  // Reset states. Deliberately NOT clearing currentBookingId here —
                  // "Cancel" only cancels the current driver's pending request (the
                  // modal immediately refetches candidates for this SAME booking so
                  // the user can pick someone else). Clearing it made the next
                  // "Assign" click send no bookingId, so the backend created a brand
                  // new booking instead of reassigning this one.
                  setIsPollingAllocation(false);
                  setAssigningCandidateId(null);
                  setAllocationStatus(null);
                  console.log('✅ [Parent] Manual allocation process stopped - assign button will be shown again');
                }}
                onFiltersChange={(filters) => {
                  setSelectedPartner(filters.selectedPartner || "all");
                  setKmRange(filters.kmRange || 50);
                  setSelectedAmbulanceTypes(filters.selectedAmbulanceTypes || []);
                }}
                onPaymentTypeChange={handlePaymentTypeChange}
              />
            )}
          </div>
        )}

        {/* Right Side - Map Background + Ambulance List */}
        <div className="relative w-1/2 min-h-screen">
          {/* Map Background - Show only when both locations are selected */}
          {showMapBackground && pickupCoordinates && dropCoordinates && (
            <div className="absolute inset-0 w-full h-full">
              <MapBackground
                center={mapCenter}
                zoom={mapZoom}
                pickupLocation={pickupCoordinates}
                dropoffLocation={dropCoordinates}
              />
            </div>
          )}

          {/* Available Ambulances List - Show at top of map section for Manual allocation */}
          {pickupCoordinates && dropCoordinates && allocationType === "Manual" && (
            <div className="absolute top-4 left-4 right-4 z-10 bg-black/60 backdrop-blur-md rounded-lg shadow-lg border border-white/30 p-4 max-h-[32rem] overflow-y-auto">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h4 className="text-sm font-semibold text-white">
                  Available Ambulances
                </h4>
                {candidatesLoading ? (
                  <span className="text-xs bg-blue-500/80 text-white px-2 py-1 rounded-full">
                    Loading...
                  </span>
                ) : candidates.length > 0 ? (
                  <span className="text-xs bg-green-500/80 text-white px-2 py-1 rounded-full">
                    {`${candidates.length} Available`}
                  </span>
                ) : candidatesError ? (
                  <span className="text-xs bg-red-500/80 text-white px-2 py-1 rounded-full">
                    Error
                  </span>
                ) : null}

                {/* Search Bar - UI based filter */}
                {candidates.length > 0 && (
                  <div className="relative ml-auto flex-shrink-0">
                    <svg className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                      type="text"
                      placeholder="Search by name, plate, driver..."
                      value={candidateSearchQuery}
                      onChange={(e) => setCandidateSearchQuery(e.target.value)}
                      className="w-56 sm:w-72 pl-9 pr-9 py-2.5 text-sm rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent backdrop-blur-sm"
                    />
                    {candidateSearchQuery && (
                      <button
                        onClick={() => setCandidateSearchQuery('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                )}
              </div>
              
              {candidatesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span className="ml-2 text-white text-sm">Loading candidates...</span>
                </div>
              ) : candidatesError ? (
                <div className="bg-red-500/20 border border-red-400/50 rounded-lg p-3">
                  <p className="text-sm text-red-200">{candidatesError}</p>
                </div>
              ) : hasSearchedCandidates && candidates.length === 0 ? (
                <div className="bg-yellow-500/20 border border-yellow-400/50 rounded-lg p-4 text-center">
                  <p className="text-sm text-yellow-200">
                    No ambulances are currently available near this location. Please try increasing your search distance or choosing a different ambulance type.
                  </p>
                </div>
              ) : !hasSearchedCandidates && candidates.length === 0 ? (
                <div className="bg-blue-500/20 border border-blue-400/50 rounded-lg p-4 text-center">
                  <p className="text-sm text-blue-200">
                    Click "Search Ambulance" to find available ambulances near your location.
                  </p>
                </div>
              ) : candidates.length > 0 && filteredCandidates.length === 0 ? (
                <div className="bg-yellow-500/20 border border-yellow-400/50 rounded-lg p-4 text-center">
                  <p className="text-sm text-yellow-200">
                    No ambulances match "{candidateSearchQuery}". Try a different search term.
                  </p>
                </div>
              ) : filteredCandidates.length > 0 ? (
                <div className="space-y-3">
                  {filteredCandidates.map((candidate, index) => {
                    const isSelected = selectedAmbulance?.vehicle_id === candidate.vehicle_id || 
                                     (selectedAmbulance?.type_id === candidate.type_id && !selectedAmbulance?.vehicle_id);
                    const isActive = allocationStatus && (
                      allocationStatus.vehicle_id === candidate.vehicle_id ||
                      (allocationStatus.type_id === candidate.type_id && !allocationStatus.vehicle_id)
                    );
                    const estimatedFare = candidate.estimated_fare || null;
                    const vendorName = candidate.operator_name;
                    
                    return (
                      <div 
                        key={candidate.vehicle_id || index}
                        className={getAmbulanceCardClassName(isSelected)}
                        onClick={() => {
                          handleAmbulanceSelect(candidate);
                        }}
                      >
                        {/* Ambulance Image */}
                        <div className="flex-shrink-0">
                          <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg flex items-center justify-center overflow-hidden">
                            <img
                              src={ambulanceImages[index % ambulanceImages.length]}
                              alt={candidate.name}
                              className="w-full h-full object-contain"
                            />
                          </div>
                        </div>
                        
                        {/* Ambulance Details */}
                        <div className="flex-1 flex items-start justify-between gap-2 min-w-0 overflow-hidden">
                          <div className="flex-1 min-w-0 overflow-hidden">
                            <div className="flex items-center gap-2">
                              <p className={getAmbulanceNameClassName(isSelected)}>
                                {candidate.name || "Ambulance"}
                              </p>
                              {candidate.is_partner && (
                                <span className="inline-flex items-center bg-green-500/80 border border-green-400/50 rounded-full px-2 py-0.5 text-xs font-medium text-white whitespace-nowrap">
                                  Partner
                                </span>
                              )}
                            </div>
                            {vendorName && (
                              <p className="text-xs text-blue-200 font-medium mt-0.5 leading-relaxed break-words">
                                {vendorName}
                              </p>
                            )}
                            {candidate.plate_number && (
                              <p className="text-xs text-gray-300 mt-0.5">
                                Plate: {candidate.plate_number}
                              </p>
                            )}
                            {/* Driver Information from API response */}
                            {candidate.driver && (
                              <div className="mt-1 space-y-0.5">
                                <div className="flex items-center gap-1">
                                  <svg className="w-3 h-3 text-blue-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                  </svg>
                                  <p className="text-xs text-blue-200 font-medium">
                                    Driver: {candidate.driver.name}
                                  </p>
                                  {candidate.driver.rating && (
                                    <span className="text-xs text-yellow-300 ml-1">
                                      ⭐ {candidate.driver.rating}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-1">
                                  <svg className="w-3 h-3 text-blue-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                  </svg>
                                  <p className="text-xs text-blue-200 font-medium">
                                    {candidate.driver.phone}
                                  </p>
                                </div>
                              </div>
                            )}
                            {candidate.distanceFromPickup !== undefined && (
                              <p className="text-xs text-blue-200 font-medium mt-0.5">
                                📍 {candidate.distanceFromPickup.toFixed(1)} km away
                                {candidate.eta_minutes !== undefined && (
                                  <span className="ml-2">• ETA: {candidate.eta_minutes} min</span>
                                )}
                              </p>
                            )}
                          </div>
                          
                          {/* Price and Status */}
                          <div className="text-right flex-shrink-0 ml-2 flex flex-col items-end relative">
                            <div className="flex items-center gap-0.5 justify-end">
                              <div className={getFareDisplayClassName(isSelected)}>
                                {isActive && allocationStatus?.estimated_fare ? 
                                  `₹${Math.round(allocationStatus.estimated_fare)}` : 
                                  estimatedFare ? `₹${Math.round(estimatedFare)}` : 
                                  "₹--"}
                              </div>
                              </div>
                            <div className="text-xs text-gray-200 whitespace-nowrap">Est. Fare</div>
                            {isSelected && (
                              <div className="text-xs text-blue-200 font-medium mt-1 whitespace-nowrap">
                                Selected
                              </div>
                            )}
                            {/* Assign Button - Show on all ambulances in manual mode */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent card click
                                console.log('📋 Assign button clicked from map section for candidate:', candidate);
                                // Track which candidate is being assigned
                                setAssigningCandidateId(candidate.vehicle_id);
                                // Select the candidate first
                                handleAmbulanceSelect(candidate);
                                // Create booking and start polling allocation status
                                handleAssignCandidate(candidate);
                              }}
                              className="mt-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                              // Disable every candidate's button while ANY assignment request is
                              // in flight — not just the clicked one — otherwise a second click on
                              // a different candidate fires before currentBookingId is set from the
                              // first response, sends no bookingId, and the backend creates a whole
                              // new booking instead of reassigning this one.
                              // Also permanently lock these once a driver has actually accepted —
                              // polling can stop for other reasons, but acceptance must never be
                              // reversible from here regardless of isPollingAllocation's value.
                              disabled={
                                isPollingAllocation ||
                                isLoading ||
                                allocationStatus?.driver_response?.status === 'ACCEPTED' ||
                                allocationStatus?.ui_state === 'ACCEPTED_GO_CONFIRM'
                              }
                            >
                              {isPollingAllocation && assigningCandidateId === candidate.vehicle_id ? (
                                <>
                                  <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                  </svg>
                                  Assigning...
                                </>
                              ) : (
                                <>
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                  Assign
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-sm text-gray-300">No candidates found. Click "Search Ambulance" to find available ambulances.</p>
                </div>
              )}
            </div>
          )}

          {/* Available Ambulances List - Show at top of map section for Automatic allocation */}
          {/* Only show when: candidate search is active, NOT after booking success */}
          {pickupCoordinates && dropCoordinates && 
           autoAllocationConfirmed && 
           allocationType === "Automatic" && 
           !successModal.isOpen && (
            <>
              {/* Modal overlay for no candidates */}
              {/* Only show this modal when candidate API was hit and returned 0 results */}
              {availableAmbulanceTypes.length === 0 ? (
                <div 
                  className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                  onClick={() => setAutoAllocationConfirmed(false)}
                >
                  <div 
                    className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Header */}
                    <div className="bg-gray-700 px-4 py-3 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <h4 className="text-sm font-semibold text-white">
                          Available Ambulances
                        </h4>
                      </div>
                      {/* Close Button */}
                      <button
                        onClick={() => setAutoAllocationConfirmed(false)}
                        className="text-gray-400 hover:text-white transition-colors p-1 rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
                        aria-label="Close modal"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    
                    {/* Content */}
                    <div className="p-4">
                      <div className="bg-yellow-500/20 backdrop-blur-md border border-yellow-400/50 rounded-lg p-4 text-center shadow-lg">
                        <p className="text-sm text-white leading-relaxed font-medium drop-shadow-sm">
                          No ambulances are currently available near this location. Please try increasing your search distance or choosing a different ambulance type.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* Regular list view when candidates are available */
                <div className="absolute top-4 left-4 right-4 z-10 bg-black/60 backdrop-blur-md rounded-lg shadow-lg border border-white/30 flex flex-col" style={{ maxHeight: 'calc(100vh - 2rem)', height: 'auto' }}>
                  {/* Fixed Header */}
                  <div className="flex items-center gap-2 p-4 pb-3 flex-shrink-0">
                    <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h4 className="text-sm font-semibold text-white">
                      Automatic Allocation
                    </h4>
                    <span className="text-xs bg-green-500/80 text-white px-2 py-1 rounded-full">
                      {`${availableAmbulanceTypes.length} Available`}
                    </span>
                  </div>
                  
                  {/* Scrollable Ambulance List - Show only 3 cards per page */}
                  {availableAmbulanceTypes.length > 0 && (() => {
                    const cardsPerPage = 3;
                    const startIndex = currentPageIndex * cardsPerPage;
                    const endIndex = Math.min(startIndex + cardsPerPage, availableAmbulanceTypes.length);
                    const visibleCards = availableAmbulanceTypes.slice(startIndex, endIndex);
                    
                    return (
                <div 
                  ref={automaticScrollContainerRef}
                  className="flex-1 overflow-y-auto px-4 pb-4 min-h-0" 
                  style={{ 
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'rgba(59, 130, 246, 0.7) rgba(0, 0, 0, 0.3)'
                  }}
                >
                  <div className="space-y-3">
                  {visibleCards.map((ambulance, relativeIndex) => {
                    // Calculate the actual index in the full array
                    const index = startIndex + relativeIndex;
                  const isActive = index === currentAutomaticIndex;
                  const isNext = index === currentAutomaticIndex + 1;
                  const isSelected = automaticSelectionConfirmed && selectedAmbulance?.type_id === ambulance.type_id;
                  const apiEstimate = fareEstimates[ambulance.type_id];
                  // Get vendor/operator name from ambulance object (candidates API) for consistency
                  // Do NOT use allocationStatus as it may be out of sync
                  const vendorName = ambulance?.operator_name ||
                    ambulance?.operator?.name ||
                    vendorNamesMap[ambulance.type_id] ||
                    null;
                  
                  // Show countdown only on active ambulance (not on next ambulance)
                  const showCountdown = isActive && !automaticSelectionConfirmed && automaticCountdown > 0;

                  // ALWAYS use driver data from ambulance object (candidates API) for consistency
                  // Do NOT use allocationStatus?.driver as it may be out of sync with currentAutomaticIndex
                  // This ensures popup and cards show the same driver info
                  const realDriver = ambulance?.driver ? {
                    name: ambulance.driver.name || 'N/A',
                    phone: ambulance.driver.phone || 'N/A',
                    rating: ambulance.driver.rating || null,
                    ambulanceNumber: ambulance.plate_number || ambulance.vehicle_id || null
                  } : null;
                  
                  // Get estimated fare - use per-vehicle fare from candidates API for consistency
                  // Do NOT use allocationStatus?.estimated_fare as it may differ from candidate fare
                  // This ensures popup and card show the same fare
                  const realEstimatedFare = ambulance?.estimated_fare ||
                    apiEstimate ||
                    null;
                  
                  // Card styling based on state
                  let cardClassName = "relative flex items-start gap-3 border rounded-lg p-2.5 transition-all overflow-hidden";
                  if (isSelected) {
                    cardClassName += " border-green-400 bg-green-500/20 backdrop-blur-sm";
                  } else if (isActive) {
                    cardClassName += " border-blue-400 bg-blue-500/20 backdrop-blur-sm ring-2 ring-blue-400 ring-opacity-50";
                  } else if (isNext) {
                    cardClassName += " border-yellow-400 bg-yellow-500/10 backdrop-blur-sm opacity-90";
                  } else {
                    cardClassName += " border-white/30 bg-black/30 backdrop-blur-sm opacity-60";
                  }
                  
                  return (
                    <div 
                      key={ambulance.type_id || index}
                      data-card-index={index}
                      className={cardClassName}
                      onClick={() => {
                        if (isActive && !automaticSelectionConfirmed) {
                          handleAutomaticAmbulanceSelect(ambulance);
                        }
                      }}
                      style={{ cursor: isActive && !automaticSelectionConfirmed ? 'pointer' : 'default' }}
                    >
                      {/* Ambulance Image */}
                      <div className="flex-shrink-0">
                        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg flex items-center justify-center overflow-hidden">
                          <img
                            src={ambulanceImages[index % ambulanceImages.length]}
                            alt={ambulance.name}
                            className="w-full h-full object-contain"
                          />
                        </div>
                      </div>
                      
                      {/* Ambulance Details */}
                      <div className="flex-1 flex items-start justify-between gap-2 min-w-0 overflow-hidden">
                        <div className="flex-1 min-w-0 overflow-hidden">
                          <div className="flex items-center gap-2 mb-1">
                            <p className={`text-sm font-semibold leading-tight break-words ${isSelected ? 'text-green-100' : isActive ? 'text-blue-100' : 'text-white/70'}`}>
                              {ambulance.name || ambulance.type}
                            </p>
                            {isActive && !automaticSelectionConfirmed && (
                              <span className="inline-flex items-center bg-blue-500/80 border border-blue-400/50 rounded-full px-2 py-0.5 text-xs font-medium text-white whitespace-nowrap animate-pulse">
                                Active
                              </span>
                            )}
                            {isNext && !automaticSelectionConfirmed && (
                              <span className="inline-flex items-center bg-yellow-500/80 border border-yellow-400/50 rounded-full px-2 py-0.5 text-xs font-medium text-white whitespace-nowrap">
                                Next
                              </span>
                            )}
                            {isSelected && (
                              <span className="inline-flex items-center bg-green-500/80 border border-green-400/50 rounded-full px-2 py-0.5 text-xs font-medium text-white whitespace-nowrap">
                                Selected
                              </span>
                            )}
                          </div>
                          
                          {vendorName && (
                            <p className="text-xs text-blue-200 font-medium mt-0.5 leading-relaxed break-words">
                              {vendorName}
                            </p>
                          )}
                          
                          {/* Driver Information - Show for all cards - Use real-time data from API */}
                          <div className="mt-1.5 space-y-0.5">
                            <div className="flex items-center gap-1.5">
                              <svg className="w-3 h-3 text-blue-200 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                              <p className="text-xs text-blue-200 font-medium truncate">
                                {isSelected && automaticSelectedDriver ? automaticSelectedDriver.name : 
                                 (isActive && isFleetUser() && allocationType === "Automatic" && allocationStatus?.driver?.name) 
                                   ? allocationStatus.driver.name 
                                   : (realDriver ? realDriver.name : 'Loading...')}
                              </p>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <svg className="w-3 h-3 text-blue-200 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                              </svg>
                              <p className="text-xs text-blue-200 font-medium truncate">
                                {isSelected && automaticSelectedDriver ? automaticSelectedDriver.phone : 
                                 (isActive && isFleetUser() && allocationType === "Automatic" && allocationStatus?.driver?.phone) 
                                   ? allocationStatus.driver.phone 
                                   : (realDriver ? realDriver.phone : 'Loading...')}
                              </p>
                            </div>
                            {/* Ambulance Number - Show real data from API */}
                            {/* For fleet users in automatic flow: Show updated vehicle number from API */}
                            {isActive && (
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <svg className="w-3 h-3 text-blue-200 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                </svg>
                                <p className="text-xs text-blue-200 font-medium truncate">
                                  {isFleetUser() && allocationType === "Automatic" && allocationStatus?.vehicle?.number 
                                    ? allocationStatus.vehicle.number 
                                    : (ambulance.plate_number || ambulance.vehicle_number || allocationStatus?.vehicle?.number || realDriver?.ambulanceNumber || 'N/A')}
                                </p>
                              </div>
                            )}
                            {!isActive && realDriver?.ambulanceNumber && (
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <svg className="w-3 h-3 text-blue-200 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                </svg>
                                <p className="text-xs text-blue-200 font-medium truncate">
                                  {realDriver.ambulanceNumber}
                                </p>
                              </div>
                            )}
                          </div>
                          
                          {ambulance.distanceFromPickup && (
                            <p className="text-xs text-blue-200 font-medium mt-1">
                              📍 {ambulance.distanceFromPickup.toFixed(1)} km away
                            </p>
                          )}
                        </div>
                        
                        {/* Timer Chip, Price, and Status */}
                        <div className="text-right flex-shrink-0 ml-2 flex flex-col items-end relative">
                          {/* Timer Chip - Show before estimated fare, only on active ambulance */}
                          {showCountdown && (
                            <div className="mb-2">
                              <span className="inline-flex items-center gap-1 bg-blue-600/80 border border-blue-400/50 rounded-full px-2 py-1 text-xs font-semibold text-white whitespace-nowrap">
                                <svg className="w-3 h-3 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span className="font-mono font-bold">{automaticCountdown}s</span>
                              </span>
                            </div>
                          )}
                          <div className="flex items-center gap-0.5 justify-end">
                            <div className={`text-sm font-bold ${isSelected ? 'text-green-100' : isActive ? 'text-blue-100' : 'text-white/70'}`}>
                              {realEstimatedFare ? `₹${Math.round(realEstimatedFare)}` : "₹--"}
                            </div>
                          </div>
                          <div className="text-xs text-gray-200 whitespace-nowrap mt-0.5">Est. Fare</div>
                          {isActive && !automaticSelectionConfirmed && (
                            <div className="mt-2 flex flex-col gap-2">
                              <div className="text-xs text-blue-200 font-medium whitespace-nowrap animate-pulse">
                                Click to Select
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRejectAmbulance();
                                }}
                                className="text-xs bg-red-600/80 hover:bg-red-700/80 text-white px-3 py-1.5 rounded-lg font-medium transition-colors"
                              >
                                Reject
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                  })}
                  </div>
                </div>
                    );
                  })()}
                  
                  {/* Fixed Footer - Help Text - Only show when there are ambulances available */}
                  {availableAmbulanceTypes.length > 0 && !automaticSelectionConfirmed && (
                    <div className="p-4 pt-3 bg-blue-900/20 border-t border-blue-400/30 flex-shrink-0">
                      <p className="text-xs text-blue-200 text-center">
                        {currentAutomaticIndex === 0 
                          ? "Click on the active ambulance to select it, or wait for automatic progression..."
                          : "Click on the active ambulance to select it"}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Placeholder when no locations selected */}
          {(!pickupCoordinates || !dropCoordinates) && (
            <div className="absolute inset-0 w-full h-full bg-gray-100 flex items-center justify-center">
              <div className="text-center text-gray-500">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-200 rounded-full flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-700 mb-2">
                  Select Pickup & Drop Locations
                </h3>
                <p className="text-sm text-gray-500">
                  Enter both pickup and dropoff locations to see the map.
                  Select "Manual" allocation to view available ambulances.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 rounded-lg p-4 z-50 max-w-sm">
          <div className="flex items-center gap-2">
            <svg
              className="w-5 h-5 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-sm text-red-700 font-medium">{error}</p>
            <button
              onClick={() => setError("")}
              className="text-red-600 hover:text-red-800"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <AmbulanceBookingConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={() =>
          !isLoading && setConfirmationModal({ isOpen: false, data: null })
        }
        onConfirm={handleConfirmBooking}
        patientName={confirmationModal.data?.patientName}
        phoneNumber={confirmationModal.data?.phoneNumber}
        pickupAddress={confirmationModal.data?.pickupAddress}
        destinationAddress={confirmationModal.data?.destinationAddress}
        ambulanceType={confirmationModal.data?.ambulanceType}
        estimatedFare={confirmationModal.data?.estimatedFare}
        bookingType={confirmationModal.data?.bookingType}
        scheduledDateTime={confirmationModal.data?.scheduledDateTime}
        isLoading={isLoading}
      />

      {/* Loading Modal */}
      {showLoadingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 text-center">
            <div className="mb-6">
              <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Creating Booking
              </h3>
              <p className="text-gray-600">
                Please wait while we process your ambulance booking...
              </p>
            </div>

            <div className="space-y-3 text-sm text-gray-500">
              <div className="flex items-center justify-center">
                <div className="w-2 h-2 bg-blue-600 rounded-full mr-2"></div>
                Processing booking details
              </div>
              <div className="flex items-center justify-center">
                <div className="w-2 h-2 bg-blue-600 rounded-full mr-2"></div>
                Assigning nearest driver
              </div>
              <div className="flex items-center justify-center">
                <div className="w-2 h-2 bg-blue-600 rounded-full mr-2"></div>
                Calculating final fare
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Success Modal */}
      <SuccessModal
        isOpen={successModal.isOpen}
        onClose={handleSuccessModalClose}
        bookingId={successModal.data?.bookingId}
        patientName={successModal.data?.patientName}
        hospitalName={successModal.data?.hospitalName}
        ambulanceType={successModal.data?.ambulanceType}
        estimatedFare={successModal.data?.estimatedFare}
        finalFare={successModal.data?.finalFare}
        driverStatus={successModal.data?.driverStatus}
        driverName={successModal.data?.driverName}
        driverPhone={successModal.data?.driverPhone}
        driverId={successModal.data?.driverId}
        vendorName={successModal.data?.vendorName}
        vendorContact={successModal.data?.vendorContact}
        vehicleNumber={successModal.data?.vehicleNumber}
        pickupAddress={successModal.data?.pickupAddress}
        destinationAddress={successModal.data?.destinationAddress}
        bookingType={successModal.data?.bookingType}
        direction={successModal.data?.direction}
        onTrackBooking={handleTrackBooking}
        matchingStatus={successModal.data?.matchingStatus}
        contactPhone={successModal.data?.contactPhone}
        contactPhone_country_code={successModal.data?.contactPhone_country_code}
        paymentStatus={successModal.data?.paymentStatus}
        paymentId={successModal.data?.paymentId}
        message={successModal.data?.message}
        scheduledDateTime={successModal.data?.scheduledDateTime}
        scheduledDate={successModal.data?.scheduledDate}
        scheduledTime={successModal.data?.scheduledTime}
      />

      {/* Validation Error Modal */}
      <ErrorModal
        isOpen={validationErrorModal.isOpen}
        onClose={() => {
          setValidationErrorModal({
            isOpen: false,
            title: "Validation Error",
            message: "",
          });
        }}
        title={validationErrorModal.title}
        message={validationErrorModal.message}
      />

      <OpenToOtherFleetsModal
        isOpen={openOthersModal.isOpen}
        preferredFleetName={openOthersModal.preferredFleetName}
        onConfirm={handleConfirmOpenToOthers}
        onCancel={handleCancelOpenToOthers}
      />

      {/* Payment Initiation Modal - Step 1 (Hospital Users Only) */}
      {!isFleetUser() && (
        <PaymentInitiationModal
          isOpen={paymentModal.isOpen}
          onClose={() => {
            setPaymentModal({
              isOpen: false,
              bookingId: null,
              bookingData: null,
              estimatedFare: null,
              patientName: null,
              pickupAddress: null,
              destinationAddress: null,
              ambulanceType: null,
            });
          }}
          bookingId={paymentModal.bookingId}
          bookingData={paymentModal.bookingData}
          estimatedFare={paymentModal.estimatedFare}
          patientName={paymentModal.patientName}
          pickupAddress={paymentModal.pickupAddress}
          destinationAddress={paymentModal.destinationAddress}
          ambulanceType={paymentModal.ambulanceType}
          onPaymentSuccess={handlePaymentSuccess}
          onError={handlePaymentError}
        />
      )}

      {/* Payment Order Creation Modal (Fleet Users Only) */}
      {isFleetUser() && (
        <PaymentOrderCreationModal
          isOpen={paymentOrderModal.isOpen}
          onClose={() => {
            setPaymentOrderModal({
              isOpen: false,
              bookingId: null,
              bookingData: null,
              estimatedFare: null,
            });
          }}
          bookingId={paymentOrderModal.bookingId}
          bookingData={paymentOrderModal.bookingData}
          dashboardType="fleet"
          onOrderCreated={(orderData) => {
            console.log('✅ [AmbulanceInstantBookingDuplicate] Payment order created, opening verification:', orderData);
            // Close order creation modal
            setPaymentOrderModal({
              isOpen: false,
              bookingId: null,
              bookingData: null,
              estimatedFare: null,
            });
            // Open verification modal with payment details
            setVerificationModal({
              isOpen: true,
              orderId: orderData.orderId,
              paymentId: orderData.paymentId,
              signature: orderData.signature,
              bookingId: paymentOrderModal.bookingId,
            });
          }}
          onError={handlePaymentError}
        />
      )}

      {/* Payment Verification Modal - Step 2 */}
      <PaymentVerificationModal
        isOpen={verificationModal.isOpen}
        onClose={() => {
          setVerificationModal({
            isOpen: false,
            orderId: null,
            paymentId: null,
            signature: null,
            bookingId: null,
          });
        }}
        orderId={verificationModal.orderId}
        paymentId={verificationModal.paymentId}
        signature={verificationModal.signature}
        bookingId={verificationModal.bookingId}
        autoVerify={true}
        dashboardType={isFleetUser() ? 'fleet' : 'hospital'}
        onVerificationSuccess={handleVerificationSuccess}
        onVerificationFailure={handleVerificationFailure}
      />

      {/* Automatic Assignment Popup */}
      <AutomaticAssignmentPopup
        isOpen={automaticAssignmentPopup.isOpen}
        onClose={handleAutomaticPopupClose}
        ambulance={automaticAssignmentPopup.ambulance}
        driver={automaticAssignmentPopup.driver}
        initialTimer={automaticAssignmentPopup.timer}
        bookingId={automaticAssignmentPopup.bookingId}
      />

      {/* Notification Container for backend error messages */}
      <NotificationContainer notification={notification} />
      </div>
    </>
  );
};

export default AmbulanceInstantBookingDuplicate;


